import axios, { Axios, AxiosRequestConfig } from "axios";
import { generateShortUUID } from "./modules/misc_objects/Utils";
import { Account, AttrType, BizDetails, BizMaster, Contact, DateFilter, FieldConfig, KVPairs, LastUpdates, Ledger, LedgerReport, LedgerType, LineItem, LoginDto, NumberRanges, OTPInfo, Payment, PaymentType, PreDefinedDateFilters, ReportFilter, ReportQueryType, SignupDTO, StockBalanceDto, StockLedger, TableConfig, Tax, TemplateConfig, Txn, TxnHeader, TxnType, ZAttributes, Item, DashboardResponse, DashboardResponseV2, BankDetail, SocialSignInDto, CacheTimeStamps, PaymentTerms, BizSettings, AppLogFilter, AppLog, BankTransactions, BulkCategoriseParams, SocialSignUpDto, RoleAccess, User, SendEmailDto, BankTransactionQueryOptions, PriceList, BizList, PendingInvite, CreateBizDTO, SendTxnWPDto, ContactBalance, TxnStatus, BranchDetails, UpdateApiKeyRequest, CreateApiKeyRequest, ApiKeyResponse, ApiKeyListResponse, ApiKey, DeleteConfirmationResponse, BulkDeleteResponse, OnboardingProgress, BulkDownloadResponse, ItemAnalytics, SocialLogins, ConfirmPaymentRequest, ConfirmPaymentResponse, SubscriptionData, CheckoutResponse, PricingRequest, PricingResponse, SubscriptionHistoryEntry, SearchResponse, DocumentListItem, StorageInfo, UploadDocumentMetadata, DeleteDocumentResponse, CustomRole, AvailablePermissions, UserProfile, SystemAccountCode, UploadHistoryResponse, BOM, BOMStatus, WorkOrder, WorkOrderStatus, WorkOrderOutput, WorkOrderLine, WorkOrderCostLine,
Project, ProjectMilestone, ProjectStatus, ProjectSummary, ProjectAttachment,
Budget, BudgetStatus, BudgetVsActualResponse, BudgetDashboardWidget,
GatewayConfig, TestGatewayConnectionResult, PublicPaymentInitResult, PublicPaymentStatus, AuditReportResponse, PeriodData, BalancesDTO, DashboardNeedsAttentionDTO,
AiUsage, OpeningBalancePreview, BankLineSuggestions, BankRule, CreateBankRuleDto, UpdateBankRuleDto, RulePreviewResult, UnmatchedLedgerItem,
RecurringTemplate, UpsertRecurringTemplateDto } from "./types";
import { CacheRequestConfig, buildWebStorage, isExpired, setupCache } from 'axios-cache-interceptor';
import { clearCachedData, setupAxios, useAuth } from "./modules/auth";
import { APIEndpoints, URLTableMapping } from "./Constants";
import { globalValue } from "./modules/apps/AppContext";
import qs from 'qs';



// Function to update the cache timestamp for a given URL
export const updateCacheTimestamp = (url: string): void => {
  // Get the current timestamp
  const currentTimestamp = new Date();

  // Check if the cache for the given URL already exists
  const existingCache = cacheTimestamps.find(cache => cache.url === url);

  if (existingCache) {
    // If the cache entry for the URL exists, update the lastRefreshed timestamp
    existingCache.lastRefreshed = currentTimestamp;
  } else {
    // If the cache entry does not exist, create a new one and push it to the array
    const newCacheEntry = new CacheTimeStamps({
      url: url,
      lastRefreshed: currentTimestamp
    });
    cacheTimestamps.push(newCacheEntry);
  }
};

const cacheTimestamps: CacheTimeStamps[] = []

// Txn lists are cached once per filter combination (see buildCacheKey), and they change on almost every write.
// Relying only on server `lastUpdates` vs client clock can leave a stale list behind after the user's own
// edit if the two clocks are skewed, so after any write we also forget the refresh time of every cached
// txn-list filter — the next fetch of each one then goes back to the server.
const BANK_LIST_URLS: string[] = [APIEndpoints.GET_BANKTXNS, APIEndpoints.GET_BANK_SUGGESTIONS, APIEndpoints.GET_UNMATCHED_LEDGER];
const TXN_LIST_URLS: string[] = [APIEndpoints.GET_TXNS, APIEndpoints.GET_TXNS_WITH_FILTERS, ...BANK_LIST_URLS, APIEndpoints.GET_BANKDETAILS];

const forgetCacheTimestamps = (urls: string[]): void => {
  for (const stamp of [...cacheTimestamps]) {
    if (urls.some(url => stamp.url?.startsWith(url))) {
      cacheTimestamps.splice(cacheTimestamps.indexOf(stamp), 1);
    }
  }
};

const forgetTxnListCacheTimestamps = (): void => forgetCacheTimestamps(TXN_LIST_URLS);

// Bank rules aren't part of the bank-transaction "tables" the server reports as updated, yet every
// bank line's ruleSuggestion is computed from them — so after a rule changes, forget the bank
// transaction lists' refresh time and the board refetches instead of showing suggestions from the old rules.
const forgetBankTxnListCacheTimestamps = (): void => forgetCacheTimestamps(BANK_LIST_URLS);

// Run as soon as any write finishes. Resetting the last-updates poll alone isn't enough: the cached
// lists are only forgotten later, inside the next poll, and only if nothing else has set the poll time
// again in the meantime (any lookup still in flight does). When that happens a list stays stale until
// the 5-minute HTTP cache expires — e.g. the bank screens after a statement upload. Forgetting here
// makes the very next fetch of each of them go back to the server.
const invalidateAfterWrite = (): void => {
  lastUpdatesCacheTimestamp = 0;
  forgetTxnListCacheTimestamps();
};

// Create an axiosInstance instance
const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_API_BASE_URL, // Replace with your backend URL
});
const cacheOptions = {
  ttl: 300000, // 5 minutes in milliseconds
  storage: buildWebStorage(localStorage, 'axios-cache:')
};

const axiosCache = setupCache(axiosInstance, cacheOptions);
setupAxios(axiosInstance)

const invalidateCache = async () => {

  const keys = Object.values(APIEndpoints); // Get all keys in the cache

  for (const key of keys) {
    const keyString = await axiosCache.storage.get(key)
    const cacheResponse = await axiosCache.storage.remove(key); // Remove each cache entry individually
  }
};

export interface UOM {
  uomId: string;
  uomNm: string;
  uomTxt: string;
}

let lastUpdatesCache: LastUpdates[] = [];
let uomCache: UOM[] = [];
let contactCache: Contact[] = [];
let txnCache: Txn[] = [];
let lastUpdatesCacheTimestamp = 0;
const CACHE_EXPIRATION_TIME = 60 * 1000; // 1 minute in milliseconds

const formatUrlWithParams = (url: string, params?: Record<string, any>): string => {
  if (!params) return url;

  Object.keys(params).forEach(key => {
    const value = encodeURIComponent(params[key]);
    url = url.replace(`:${key}`, value);
  });
  return url;
};

function checkFormDataForFiles(formData: FormData): boolean {
  for (const key of formData.keys()) {
    const value = formData.get(key);
    if (value instanceof File) {
      return true; // FormData contains at least one file
    }
  }
  return false; // No files in FormData
}

function clearFileCache() {
  Object.keys(fileCache).forEach((key) => delete fileCache[key]); // Clear the cache
}

async function postWithFile(url: string, formData: any, headers: any) {
  try {
    // Check if the FormData contains any files
    const hasFiles = formData instanceof FormData ? checkFormDataForFiles(formData) : false;

    const response = await postWithCacheClear(url, formData, headers);

    // Clear the file cache only if the formData contains files
    if (hasFiles) {
      clearFileCache();
    }
    invalidateAfterWrite();
    return response;
  } catch (error: any) {
    // Check if the error is an AxiosError and handle accordingly
    if (error.response) {
      // Log detailed error from the response
      const errorMessages = Array.isArray(error.response.data.message)
        ? error.response.data.message
        : [error.response.data.message];

      const formattedMessage = errorMessages.join('\n');
      throw new Error(formattedMessage);
    } else {
      console.error('Error occurred while posting the data:', error.message);
      throw error;
    }
  }
}

async function postWithCacheClear<T = any>(
  url: string,
  data: any,
  headers?: any
): Promise<T> {
  try {
    const response = await axiosInstance.post(url, data, headers);

    // Clear last updates cache (same behavior as file version)
    invalidateAfterWrite();

    return response.data;
  } catch (error: any) {
    if (error.response) {
      // Extract meaningful error message
      const errorMessages = Array.isArray(error.response.data?.message)
        ? error.response.data.message
        : [error.response.data?.message || "Something went wrong"];

      const formattedMessage = errorMessages.join("\n");
      throw new Error(formattedMessage);
    } else {
      console.error("Error occurred while posting the data:", error.message);
      throw error;
    }
  }
}

async function putWithCacheClear<T = any>(
  url: string,
  data: any,
  headers?: any
): Promise<T> {
  try {
    const response = await axiosInstance.put(url, data, headers);

    // Clear last updates cache (same behavior as post version)
    invalidateAfterWrite();

    return response.data;
  } catch (error: any) {
    if (error.response) {
      // Extract meaningful error message
      const errorMessages = Array.isArray(error.response.data?.message)
        ? error.response.data.message
        : [error.response.data?.message || "Something went wrong"];

      const formattedMessage = errorMessages.join("\n");
      throw new Error(formattedMessage);
    } else {
      console.error("Error occurred while putting the data:", error.message);
      throw error;
    }
  }
}

async function patchWithCacheClear<T = any>(
  url: string,
  data: any,
  headers?: any
): Promise<T> {
  try {
    const response = await axiosInstance.patch(url, data, headers);

    // Clear last updates cache (same behavior as post/put versions)
    invalidateAfterWrite();

    return response.data;
  } catch (error: any) {
    if (error.response) {
      const errorMessages = Array.isArray(error.response.data?.message)
        ? error.response.data.message
        : [error.response.data?.message || "Something went wrong"];

      const formattedMessage = errorMessages.join("\n");
      throw new Error(formattedMessage);
    } else {
      console.error("Error occurred while patching the data:", error.message);
      throw error;
    }
  }
}

async function putWithCacheUpdate<T>(
  url: string,                    // URL for the PUT request
  data: Partial<T>,                // Data to be sent in the PUT request body
  queryParams?: Record<string, any>, // Optional query parameters
): Promise<T | void> {
  try {
    // Construct the full URL with query parameters if provided
    const queryString = new URLSearchParams(queryParams).toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;

    // Perform the PUT request with the provided data
    const response = await axiosInstance.put<T>(fullUrl, data);

    // Reset the cache timestamp on success
    if (response.status === 200) {
      invalidateAfterWrite();
      return response.data; // Return the updated resource if needed
    } else {
      console.error('Failed to update resource, response status:', response.status);
    }
  } catch (error) {
    console.error('Error during PUT request:', error);
    throw error; // Rethrow the error for further handling
  }
}



async function deleteWithCacheUpdate<T>(
  url: string,                    // URL for the DELETE request
  data?: any,                     // Optional request body data for DELETE
  queryParams?: Record<string, any>, // Optional query parameters
): Promise<T> {
  try {
    // Construct the full URL with query parameters if provided
    const queryString = queryParams ? new URLSearchParams(queryParams).toString() : '';
    const fullUrl = queryString ? `${url}?${queryString}` : url;

    // Perform the DELETE request with optional data in body
    const response = await axiosInstance.delete<T>(fullUrl, data ? { data } : undefined);

    // Reset cache timestamp on successful delete
    invalidateAfterWrite();

    // Return response data
    return response.data;

  } catch (error: any) {
    console.error('Error during delete request:', error);

    // Enhanced error handling
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      switch (status) {
        case 400:
          throw new Error(data?.message || 'Bad Request - Invalid delete confirmation code or missing required fields');
        case 401:
          throw new Error(data?.message || 'Unauthorized - Invalid delete confirmation code');
        case 404:
          throw new Error(data?.message || 'Not Found - Business or delete request not found');
        case 410:
          throw new Error(data?.message || 'Gone - Delete confirmation code has expired');
        case 500:
          throw new Error(data?.message || 'Internal Server Error - Please try again later');
        default:
          throw new Error(data?.message || `Delete request failed with status ${status}`);
      }
    } else if (error.request) {
      // Network error - no response received
      throw new Error('Network error - Unable to connect to server. Please check your internet connection.');
    } else {
      // Something else happened
      throw new Error(error.message || 'An unexpected error occurred during deletion');
    }
  }
}




export const fetchDataWithCacheOption = async <T>(url: string, getRecent?: boolean, pathParams?: Record<string, any>, queryParams?: Record<string, any>): Promise<T> => {
  try {
    const formattedUrl = formatUrlWithParams(url, pathParams);
    const cacheOption = getRecent ? false : undefined;
    const config: CacheRequestConfig = {
      params: queryParams,
      id: formattedUrl,
      cache: cacheOption
    };
    if (getRecent) {
      await axiosCache.storage.remove(formattedUrl);
    }
    const response = await axiosCache.get(formattedUrl, config);
    return response.data;
  } catch (error) {
    console.error(`Error fetching data from ${url}:`, error);
    throw error;
  }
};


export const fetchData = async <T>(url: string, pathParams?: string[], queryParams?: Record<string, any>, silent?: boolean): Promise<T> => {
  try {
    const formattedUrl = `${url}${pathParams && pathParams.length > 0 ? '/' + pathParams.map(encodeURIComponent).join('/') : ''}`;
    const cacheConfig: false | undefined = URLTableMapping.some(urlTableMap => urlTableMap.url === url) ? undefined : false;
    const tables = URLTableMapping.find(urlTableMap => urlTableMap.url === url)?.tables
    const cacheKey = buildCacheKey(url, pathParams, queryParams);
    const cacheExpiry = await isCacheExpired(cacheKey, tables) ? false : undefined
    const config: CacheRequestConfig = {
      params: queryParams,
      id: cacheKey,
      cache: cacheConfig
    };
    if (silent) (config as any).__silent = true;

    if (cacheExpiry === false) {
      await axiosCache.storage.remove(cacheKey, config);
    }

    const response = await axiosCache.get(formattedUrl, config);
    if (cacheExpiry === false) {
      updateCacheTimestamp(cacheKey)
    }
    return response.data;
  } catch (error) {
    handleApiError(error)
  }
};


// PATCH data implementation
export const patchData = async <T>(
  url: string,
  data?: any,
  pathParams?: string[],
  queryParams?: Record<string, any>
): Promise<T> => {
  try {
    const formattedUrl = `${url}${pathParams && pathParams.length > 0 ? '/' + pathParams.map(encodeURIComponent).join('/') : ''}`;

    // For PATCH requests, we typically want to invalidate cache after successful update
    const tables = URLTableMapping.find(urlTableMap => urlTableMap.url === url)?.tables;

    const config = {
      params: queryParams,
    };

    const response = await axiosCache.patch(formattedUrl, data, config);
    invalidateAfterWrite();

    // Invalidate cache for related tables after successful PATCH
    if (tables && tables.length > 0) {
      // await invalidateCacheForTables(tables);
    }

    return response.data;
  } catch (error) {
    console.error(`Error patching data to ${url}:`, error);
    throw error;
  }
};

// DELETE data implementation
export const deleteData = async <T>(
  url: string,
  pathParams?: string[],
  queryParams?: Record<string, any>
): Promise<T> => {
  try {
    const formattedUrl = `${url}${pathParams && pathParams.length > 0 ? '/' + pathParams.map(encodeURIComponent).join('/') : ''}`;

    // For DELETE requests, we typically want to invalidate cache after successful deletion
    const tables = URLTableMapping.find(urlTableMap => urlTableMap.url === url)?.tables;

    const config = {
      params: queryParams,
    };

    const response = await axiosCache.delete(formattedUrl, config);
    invalidateAfterWrite();

    // Invalidate cache for related tables after successful DELETE
    if (tables && tables.length > 0) {
      // await invalidateCacheForTables(tables);
    }

    return response.data;
  } catch (error) {
    console.error(`Error deleting data from ${url}:`, error);
    throw error;
  }
};



const clearCacheIfNecessary = (lastUpdatesCurrent: LastUpdates[]): void => {
  lastUpdatesCurrent.forEach((lastUpdate) => {
    if (
      lastUpdate.updatedAt >
      (lastUpdatesCache.find((update) => update.tableName === lastUpdate.tableName)?.updatedAt ?? 0)
    ) {
      switch (lastUpdate.tableName) {
        case 'uoms':
          // uomCache = [];
          break;
        case 'contacts':
          contactCache = [];
          break;
        // Add cases for other tables if necessary
        default:
          break;
      }
    }
  });
};

export const isCacheExpired = async (url: string, tables?: string[]): Promise<boolean> => {
  let lastUpdates: any[] = []; // Declare lastUpdates outside try-catch block

  try {
    // Fetch the last updates data (likely from an API or database)
    lastUpdates = await fetchLastUpdates();
    // Handle the fetched data if needed
  } catch (error) {
    // Handle the error
    console.error('Error fetching last updates:', error);
    // Return true if there is an error fetching last updates
    return true;
  }

  // Find the cache detail for the specific URL
  const urlCacheDetail = cacheTimestamps.find(cacheTimeStamp => cacheTimeStamp.url === url);

  if (!urlCacheDetail || !tables) {
    return true;
  }

  // Loop through the tables in the cache detail
  for (const table of tables) {

    // Get the last update timestamp for the table from lastUpdates
    const lastUpdate = lastUpdates.find(lastUpdate => lastUpdate.tableName === table);
    // If there is no last update info for this table or the cache timestamp is older, cache is expired
    if (lastUpdate! && (new Date(urlCacheDetail?.lastRefreshed || 0).getTime()) < new Date(lastUpdate.updatedAt).getTime()) {
      return true;
    } else if (urlCacheDetail?.lastRefreshed) {
      // If last update is null (server has no data) but lastRefreshed is non-zero, assume server has no data, so cache is valid
      continue;
    }
  }
  // If all timestamps are up-to-date, cache is not expired
  return false;
};

const buildCacheKey = (
  url: string,
  pathParams?: string[],
  queryParams?: Record<string, any>
): string => {

  const path =
    pathParams && pathParams.length > 0
      ? '/' + pathParams.map(p => encodeURIComponent(String(p))).join('/')
      : '';

  let query = '';

  if (queryParams && Object.keys(queryParams).length > 0) {
    const sortedKeys = Object.keys(queryParams)
      .filter(k => queryParams[k] !== undefined && queryParams[k] !== null)
      .sort();

    const queryString = sortedKeys
      .map(key => {
        const value = queryParams[key];

        if (Array.isArray(value)) {
          return value
            .map(v => `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`)
            .join('&');
        }

        if (typeof value === 'object') {
          return `${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`;
        }

        return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
      })
      .join('&');

    if (queryString) {
      query = `?${queryString}`;
    }
  }

  return `${url}${path}${query}`;
};


export const fetchLastUpdates = async (): Promise<LastUpdates[]> => {
  const currentTime = Date.now();
  // lastUpdatesCacheTimestamp === 0 means a write happened since the last poll (every mutation resets it)
  if (lastUpdatesCacheTimestamp === 0) forgetTxnListCacheTimestamps();
  const getRecentUpdates = ((currentTime - lastUpdatesCacheTimestamp) > CACHE_EXPIRATION_TIME || lastUpdatesCacheTimestamp === 0)
  const lastUpdates = await fetchDataWithCacheOption<LastUpdates[]>('/appservice/getLastUpdates', getRecentUpdates);
  lastUpdatesCacheTimestamp = currentTime; // Update the timestamp
  return lastUpdates;
};

export const fetchUoms = async (): Promise<UOM[]> => {
  uomCache = await fetchData<UOM[]>('/masterdata/getUoms');
  return uomCache;
};


export const fetchStockBalance = async (reportFilter: ReportFilter): Promise<StockBalanceDto[]> => {
  const queryParams: Record<string, any> = {};

  if (reportFilter) {
    // Format the date as a string (e.g., YYYY-MM-DD)
    queryParams['reportFilter'] = JSON.stringify(reportFilter)
  }
  const stockBalances = await fetchData<StockBalanceDto[]>('inventory/getStockBalances', undefined, queryParams);
  return stockBalances;
};

export const fetchContactTxns = async (reportFilter: ReportFilter): Promise<ContactTxnsForReport> => {
  const queryParams: Record<string, any> = {};

  if (reportFilter) {
    // Format the date as a string (e.g., YYYY-MM-DD)
    queryParams['reportFilter'] = JSON.stringify(reportFilter)
  }

  const contactTxns = await fetchData<ContactTxnsForReport>('/reporting/contact-txns', undefined, queryParams);

  return contactTxns;
};


export const fetchStockLedger = async (reportFilter: ReportFilter): Promise<StockLedger[]> => {
  const predefinedDateFilter = reportFilter.dateFilter.setPredefinedDateFilter
  const queryParams: { [key: string]: any } = { predefinedDateFilter };
  queryParams.fromDate = reportFilter.dateFilter.fromDate
  queryParams.toDate = reportFilter.dateFilter.toDate
  queryParams.itemFilter = reportFilter.itemFilter
  queryParams.selectedAccount = reportFilter.selectedAccount
  queryParams.reportFilter = JSON.stringify(reportFilter)
  const url = 'inventory/getStockLedger'

  return fetchData<StockLedger[]>(url, undefined, queryParams);
}


export const upSertUom = async (uom: UOM): Promise<UOM> => {
  const response = await postWithFile('/masterdata/upsertUom', uom, undefined);
  return response;
};




export const fetchAccounts = async (silent?: boolean): Promise<Account[]> => {
  return fetchData<Account[]>('/masterdata/getAccounts', undefined, undefined, silent);
};

export const checkActivationCode = async (activationCode: string): Promise<boolean> => {
  return fetchData<boolean>('/auth/check-activation-code', [activationCode]);
};

// Update the function signature to accept optional socialSignIn parameter
export const activateUser = async (activationCode?: string, password?: string, socialSignIn?: SocialLogins, sToken?: string) => {
  try {
    const response = await postWithFile('/auth/activate-user', {
      activationCode: activationCode,
      password: password,
      socialSignIn: socialSignIn,
      sToken: sToken
    }, undefined)
    return response;
  } catch (error) {
    handleApiError(error)
  }
};

export const passwordUpdate = async (forgotPasswordCode?: string, password?: string) => {
  const response = await postWithFile('/auth/updatePassword', {
    forgotPasswordCode: forgotPasswordCode,
    newPassword: password
  }, undefined)
  return response;
};

export const checkForgotPasswordCode = async (forgotPasswordCode: string): Promise<boolean> => {
  return fetchData<boolean>('/auth/check-forgot-password-code', [forgotPasswordCode]);
};

export const fetchAccount = async (accountId: string): Promise<Account> => {
  return fetchDataWithCacheOption<Account>('/masterdata/getAccount/' + accountId, true);
};

export const upsertAccount = async (account: Account) => {
  const response = await postWithFile('/masterdata/upsertAccount/', account, undefined);
  return response;
};

export const deleteAccount = async (account: Account) => {
  try {
    const response = await postWithFile('/masterdata/deleteAccount/', account, undefined);
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};

export const deleteAccounts = async (accountIds: string[]) => {
  try {
    const response = await postWithFile('/masterdata/deleteAccounts', accountIds, undefined);
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};

export const updateSystemAcctCode = async (
  systemAccountCode: SystemAccountCode,
  newAccountId: string,
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await putWithCacheClear<{ success: boolean; message: string }>(
      '/masterdata/updateSystemAcctCode',
      { systemAccountCode, newAccountId },
    );
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};

export const upsertTax = async (tax: Tax) => {
  try {
    const response = await postWithFile(`/masterdata/upsertTax`, tax, undefined);
    return response;
  } catch (error: any) {
    handleApiError(error)
  }
};

export const fetchTax = async (taxId: string): Promise<Tax> => {
  return fetchDataWithCacheOption<Tax>(`/masterdata/getTax/${taxId}`, true);
};

export const fetchNumberRanges = async (): Promise<NumberRanges[]> => {
  return fetchDataWithCacheOption<NumberRanges[]>('/appservice/getNumberRanges', true);
};

export const fetchNumberRange = async (object: string): Promise<NumberRanges> => {
  return fetchDataWithCacheOption<NumberRanges>(`/appservice/getNumberRange/${object}`, true);
};

export const upsertNumberRange = async (numberRange: NumberRanges) => {
  const sanitizedNumberRange = { ...numberRange };
  const response = await postWithCacheClear(`/appservice/upsertNumberRange`, sanitizedNumberRange);
  return response;
};


export const fetchTaxes = async (): Promise<Tax[]> => {
  return fetchData<Tax[]>(`/masterdata/getTaxes`);
};

export const deleteTax = async (taxId: string): Promise<any> => {
  try {
    const response = await  deleteWithCacheUpdate<string>(`/masterdata/deleteTax/${taxId}`);
    return response;
  } catch (error: any) {
    handleApiError(error)
  }
};

export const fetchZAttributes = async (attrType: AttrType): Promise<ZAttributes[]> => {
  const attrTypeId = `${attrType}`.trim();
  return fetchData<ZAttributes[]>(`/masterdata/getZAttributes`, [attrTypeId.replace(/\s/g, '')], undefined);
};

export const fetchPaymentTerms = async (): Promise<PaymentTerms[]> => {
  return fetchData<PaymentTerms[]>(`/masterdata/getPaymentTerms`);
};

export const upSertZAttribute = async (attribute: ZAttributes): Promise<ZAttributes> => {
  const response = await postWithFile('/masterdata/upsertZattribute', attribute, undefined);
  return response;
};

export const upsertUser = async (user: User): Promise<User> => {
  const response = await postWithFile('/auth/upsertUser', user, undefined);
  return response;
};

export const upSertPaymentTerm = async (paymentTerm: PaymentTerms): Promise<PaymentTerms> => {
  const response = await postWithFile('/masterdata/upsertPaymentTerm', paymentTerm, undefined);
  return response;
};

export const upsertBizSettings = async (bizSettings: BizSettings): Promise<BizSettings> => {
  const response = await postWithFile('/appservice/upsertBizSettings', bizSettings, undefined);
  return response;
};

export const fetchSeenFeatures = async (): Promise<string[]> => {
  return fetchData<string[]>('/appservice/seenFeatures', undefined, undefined, true);
};

export const markFeatureSeen = async (featureKey: string): Promise<string[]> => {
  const response = await postWithFile('/appservice/markFeatureSeen', { featureKey }, undefined);
  return response;
};

export const applyLateFee = async (
  txnIds: string[]
): Promise<{ applied: { txnId: string; txnNo: string; feeAmt: number }[]; skipped: { txnId: string; txnNo?: string; reason: string }[] }> => {
  return postWithCacheClear('/appservice/apply-late-fee', { txnIds });
};

export const reverseLateFee = async (
  txnId: string,
  postingId: string,
  reason?: string
): Promise<Txn> => {
  return postWithCacheClear(`/txndata/${txnId}/reverse-late-fee`, { postingId, reason });
};

// ── e-Invoice (India GST) ─────────────────────────────────────────────────────

async function parseEInvoiceBlobError(error: any): Promise<never> {
  const blobData: Blob | undefined =
    error.response?.data instanceof Blob ? error.response.data : undefined;
  if (blobData) {
    let parsed: any;
    try { parsed = JSON.parse(await blobData.text()); } catch { /* not JSON */ }
    const message = parsed
      ? (Array.isArray(parsed.message) ? parsed.message.join('\n') : parsed.message)
      : undefined;
    const err: any = new Error(message || 'e-Invoice request failed');
    if (Array.isArray(parsed?.missingFields)) err.missingFields = parsed.missingFields;
    throw err;
  }
  throw error;
}

export const exportEInvoice = async (txnId: string, txnNo: string): Promise<void> => {
  try {
    const { data } = await fetchBlobData(`/txndata/einvoice/export/${txnId}`);
    const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `einv-${txnNo}.json`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (error: any) {
    await parseEInvoiceBlobError(error);
  }
};

export const importSignedEInvoice = async (txnId: string, file: File): Promise<{ irn: string; irnAckNo: string; irnAckDate: string }> => {
  const formData = new FormData();
  formData.append('signed_json', file);
  return postWithCacheClear(`/txndata/einvoice/import/${txnId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const bulkExportEInvoice = async (txnIds: string[]): Promise<void> => {
  try {
    const { data } = await postBlobData('/txndata/einvoice/export/bulk', { txnIds });
    const blob = data instanceof Blob ? data : new Blob([data as any]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `einvoice-export-${new Date().toISOString().split('T')[0]}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (error: any) {
    await parseEInvoiceBlobError(error);
  }
};

export const bulkImportSignedEInvoice = async (files: File[]): Promise<Array<{ txnNo: string; irn?: string; success: boolean; error?: string }>> => {
  const formData = new FormData();
  files.forEach(file => formData.append('signed_jsons[]', file));
  return postWithCacheClear('/txndata/einvoice/import/bulk', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const upSertItem = async (item: Item, image?: File) => {
  try {
    const formData = new FormData();
    // Append the contact object as a JSON string
    formData.append('itemDto', JSON.stringify(item));

    if (image) {
      formData.append('files', image);
    }
    // Make the request using formData
    const response = await postWithFile('/masterdata/upsertItemwFile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    handleApiError(error)
  }
};

export const requestForgotPassword = async (email: string) => {
  const response = await postWithFile('/auth/forgotPasswordRequest', {
    email: email
  }, undefined)
  return response
}

export const fetchItems = async (silent?: boolean): Promise<Item[]> => {
  return fetchData<Item[]>(APIEndpoints.GET_ITEMS, undefined, undefined, silent);
};

// Utility to convert a stream to a string or buffer
const streamToString = (stream: any): Promise<string> => {
  const chunks: any[] = [];
  return new Promise((resolve, reject) => {
    stream.on('data', (chunk: any) => chunks.push(chunk));
    stream.on('error', reject);
    stream.on('end', () => resolve(Buffer.concat(chunks).toString('utf-8')));
  });
};

const fileCache: { [url: string]: Blob } = {}; // In-memory cache object

async function postBlobData(url: string, body: any): Promise<{ data: Blob; headers: Record<string, string> }> {
  const response = await axiosInstance.post(url, body, {
    responseType: 'blob',
    headers: { 'Content-Type': 'application/json' },
    cache: false,
  } as CacheRequestConfig);
  return { data: response.data, headers: response.headers as Record<string, string> };
}

async function fetchBlobData(url: string, options?: { params?: Record<string, any>; responseType?: 'blob' | 'arraybuffer'; timeout?: number }): Promise<{ data: ArrayBuffer | Blob; headers: Record<string, string> }> {
  const response = await axiosInstance.get(url, {
    responseType: options?.responseType ?? 'blob',
    params: options?.params,
    timeout: options?.timeout,
    cache: false, // Binary data cannot be serialized to localStorage cache
  } as CacheRequestConfig);
  return { data: response.data, headers: response.headers as Record<string, string> };
}

const fetchFileWithCache = async (url: string): Promise<Blob> => {
  try {
    // Check if the file is already cached
    if (fileCache[url]) {
      return fileCache[url]; // Return the cached file
    }

    // If not cached, fetch the file as a blob (for binary data like images)
    const fileResponse = await axiosInstance.get(url, {
      responseType: 'blob', // Ensures the response is treated as binary (Blob)
    });

    const fileBlob = fileResponse.data as Blob;

    // Cache the file
    fileCache[url] = fileBlob;

    return fileBlob;
  } catch (error) {
    console.error('Error fetching file:', error);
    throw error;
  }
};



export const fetchItem = async (itemId: string): Promise<{ item: Item, files: Blob[] }> => {
  try {
    const item = await fetchDataWithCacheOption<Item>(`/masterdata/getItem/${itemId}`, true);
    const files: Blob[] = [];

    if (item.itemImages && item.itemImages.length > 0) {
      for (const image of item.itemImages) {
        const encodedImageURL = encodeURIComponent(image);

        // Fetch the file and return it as a Blob
        const fileResponse = await fetchFileWithCache(`/utils/getFileByKey/${encodedImageURL}`);

        // Push the blob response to the files array
        files.push(fileResponse as Blob); // fileResponse.data will be the Blob
      }
    }

    return { item, files };
  } catch (error) {
    console.error('Error fetching item:', error);
    throw error;
  }
};

export const upSertContact = async (contact: Contact) => {
  const response = await postWithFile('/masterdata/upsertContact', contact, undefined);
  return response;
};

export const sendPortalInvite = async (contactId: string) => {
  const response = await postWithFile(`/portal/contacts/${contactId}/invite`, {}, undefined);
  return response;
};

export const upSertContactWithImage = async (contact: Contact, image?: File) => {
  try {
    const formData = new FormData();
    // Append the contact object as a JSON string
    formData.append('contact', JSON.stringify(contact));

    // Append the image file if it exists
    if (image) {
      formData.append('file', image);
    }

    // Make the request using formData
    const response = await postWithFile('/masterdata/upsertContactwFile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response;  // Return the successful response data

  } catch (error: any) {
    handleApiError(error);
  }
};

export const fetchContacts = async (balanceDue?: boolean, silent?: boolean): Promise<Contact[]> => {
  const queryParams: { [key: string]: any } = {};
  if (balanceDue) queryParams['balanceDue'] = true
  return fetchData<Contact[]>(APIEndpoints.GET_CONTACTS, undefined, queryParams, silent);
};

export const fetchRecTxns = async (txnType: TxnType): Promise<Txn[]> => {
  const url = '/txndata/getRecTxns'
  return fetchData<Txn[]>(url, [txnType]);
}

export const fetchAllRecurringPlans = async (): Promise<Txn[]> => {
  return fetchData<Txn[]>(APIEndpoints.GET_REC_TXNS_ALL);
}

export const fetchRecurrenceInstances = async (txnId: string): Promise<Txn[]> => {
  return fetchData<Txn[]>('/txndata/recurrenceInstances', [txnId]);
}

export const fetchRecurrencePreview = async (txnId: string, count = 3): Promise<{ dates: Date[] }> => {
  return fetchData<{ dates: Date[] }>('/txndata/recurrencePreview', [txnId], { count });
}

export const updateRecurrenceSchedule = async (txnId: string, dto: import('./types').UpdateRecurrenceSettings): Promise<void> => {
  await patchData('/txndata/recurrence', dto, [txnId]);
}

export const billNow = async (txnId: string): Promise<{ txnNo: string }> => {
  const response = await axiosCache.post(`/txndata/recurrenceBillNow/${encodeURIComponent(txnId)}`);
  lastUpdatesCacheTimestamp = 0;
  return response.data;
}

export const runRecurrenceForBiz = async (): Promise<{ success: any[]; skipped: any[]; failed: any[] }> => {
  const response = await axiosCache.post('/txndata/runRecurrenceForBiz');
  lastUpdatesCacheTimestamp = 0;
  return response.data;
}

// ====================================================================================
// MBAI-150: RecurringTemplate — additive, alongside the legacy recurrence functions
// above (which keep serving pre-existing production recurring series).
// ====================================================================================

export const fetchRecurringTemplates = async (): Promise<RecurringTemplate[]> => {
  return fetchData<RecurringTemplate[]>('/txndata/recurring-templates');
}

export const createRecurringTemplate = async (dto: UpsertRecurringTemplateDto): Promise<RecurringTemplate> => {
  const response = await axiosCache.post('/txndata/recurring-templates', dto);
  lastUpdatesCacheTimestamp = 0;
  return response.data;
}

export const updateRecurringTemplate = async (templateId: string, dto: UpsertRecurringTemplateDto): Promise<void> => {
  await patchData('/txndata/recurring-templates', dto, [templateId]);
}

export const deleteRecurringTemplate = async (templateId: string): Promise<void> => {
  await deleteData('/txndata/recurring-templates', [templateId]);
}

export const pauseRecurringTemplate = async (templateId: string): Promise<void> => {
  await axiosCache.post(`/txndata/recurring-templates/${encodeURIComponent(templateId)}/pause`);
  lastUpdatesCacheTimestamp = 0;
}

export const resumeRecurringTemplate = async (templateId: string): Promise<void> => {
  await axiosCache.post(`/txndata/recurring-templates/${encodeURIComponent(templateId)}/resume`);
  lastUpdatesCacheTimestamp = 0;
}

export const fetchRecurringTemplateInstances = async (templateId: string): Promise<Txn[]> => {
  return fetchData<Txn[]>('/txndata/recurring-templates', [templateId, 'instances']);
}

export const fetchRecurringTemplatePreview = async (templateId: string, count = 3): Promise<{ dates: Date[] }> => {
  return fetchData<{ dates: Date[] }>('/txndata/recurring-templates', [templateId, 'preview'], { count });
}

export const billNowFromTemplate = async (templateId: string): Promise<{ txnNo: string }> => {
  const response = await axiosCache.post(`/txndata/recurring-templates/${encodeURIComponent(templateId)}/bill-now`);
  lastUpdatesCacheTimestamp = 0;
  return response.data;
}

export const runRecurringTemplatesForBiz = async (): Promise<{ success: any[]; skipped: any[]; failed: any[] }> => {
  const response = await axiosCache.post('/txndata/recurring-templates/run-for-biz');
  lastUpdatesCacheTimestamp = 0;
  return response.data;
}

export const fetchTxns = async (dateFilter: DateFilter, txnType: TxnType, txnStatus?: TxnStatus[]): Promise<Txn[]> => {
  const pathParams = { txnType };
  const url = APIEndpoints.GET_TXNS; // cached per txnType + dateFilter + txnStatus (see URLTableMapping)
  const predefinedDateFilter = dateFilter.setPredefinedDateFilter
  const queryParams: { [key: string]: any } = {};
  if (dateFilter) {
    // Format the date as a string (e.g., YYYY-MM-DD)
    queryParams['dateFilter'] = JSON.stringify(dateFilter)
  }
  if (txnStatus) {
    // Format the date as a string (e.g., YYYY-MM-DD)
    queryParams['txnStatus'] = JSON.stringify(txnStatus)
  }

  txnCache = await fetchData<Txn[]>(url, [txnType], queryParams);
  return txnCache;
};

export const fetchPaymentsByTxnId = async (txnId: string): Promise<Payment[]> => {
  try {
    // Validate txnId parameter
    if (!txnId || txnId.trim() === '') {
      throw new Error('Transaction ID is required');
    }

    const response = await fetchData<Payment[]>(`/txndata/getPaymentsByTxnId/${encodeURIComponent(txnId)}`);
    return response;
  } catch (error: any) {
    // Handle 204 No Content specifically - return empty array
    if (error.response?.status === 204) {
      return [];
    }

    // Use existing error handler for all other cases
    handleApiError(error);
  }
};

export const fetchPaymentsByReturnId = async (returnTxnId: string): Promise<Payment[]> => {
  try {
    // Validate returnTxnId parameter
    if (!returnTxnId || returnTxnId.trim() === '') {
      throw new Error('Return Transaction ID is required');
    }

    const response = await fetchData<Payment[]>(`/txndata/getPaymentsByReturnId/${encodeURIComponent(returnTxnId)}`);
    return response;
  } catch (error: any) {
    // Handle 204 No Content specifically - return empty array
    if (error.response?.status === 204) {
      return [];
    }

    // Use existing error handler for all other cases
    handleApiError(error);
  }
};

export const fetchPayments = async (dateFilter: DateFilter, paymentType: PaymentType, branchId?: string, tagsFilter?: string[]): Promise<Payment[]> => {
  const pathParams = { paymentType };
  const url = '/txndata/getPayments'; // Define the URL with placeholders
  const predefinedDateFilter = dateFilter.setPredefinedDateFilter
  const queryParams: { [key: string]: any } = { predefinedDateFilter };
  if (dateFilter) {
    // Format the date as a string (e.g., YYYY-MM-DD)
    queryParams['dateFilter'] = JSON.stringify(dateFilter)
  }
  if (branchId) {
    queryParams['branchId'] = branchId
  }
  if (tagsFilter?.length) {
    queryParams['tagsFilter'] = tagsFilter
  }

  return fetchData<Payment[]>(url, [paymentType], queryParams);
};

export const fetchLedgers1 = async (ledgerType: LedgerType, dateFilter?: DateFilter,): Promise<Ledger[]> => {
  const pathParams = { ledgerType };
  const url = '/ledger/getLedgers'; // Define the URL with placeholders
  const reportFilter = new ReportFilter({ dateFilter: dateFilter, ledgerType: ledgerType })
  const queryParams: { [key: string]: any } = {};
  if (dateFilter) {
    // Format the date as a string (e.g., YYYY-MM-DD)
    queryParams['reportFilter'] = JSON.stringify(reportFilter)
  }
  return fetchData<Ledger[]>(url, undefined, queryParams);
};


export const fetchLedgers = async (dateFilter: DateFilter, ledgerType: LedgerType,): Promise<Ledger[]> => {
  const url = '/ledger/getLedgers/'; // Define the URL with placeholders
  const predefinedDateFilter = dateFilter.setPredefinedDateFilter
  const queryParams: { [key: string]: any } = { predefinedDateFilter };
  queryParams.fromDate = dateFilter.fromDate
  queryParams.toDate = dateFilter.toDate
  queryParams.ledgerType = ledgerType


  return fetchData<Ledger[]>(url, undefined, queryParams);
};


export const fetchTxnsWithFilters = async (
  dateFilter: DateFilter,
  txnType?: TxnType | TxnType[],
  itemList?: string[],
  additionalFilters?: Record<string, any>,
  branchId?: string,
  tagsFilter?: string[]
): Promise<Txn[]> => {
  const pathParams = { txnType };
  const url = APIEndpoints.GET_TXNS_WITH_FILTERS; // cached per full filter set (see URLTableMapping)

  const queryParams = {
    txnType: Array.isArray(txnType) ? txnType.join(',') : txnType,
    fromDate: dateFilter.fromDate,
    toDate: dateFilter.toDate,
    branchId: branchId,
    itemList,
    tagsFilter: tagsFilter,
    ...additionalFilters
  };
  txnCache = await fetchData<Txn[]>(url, undefined, queryParams);
  return txnCache;
};

export const getTxn = async (txnId: string): Promise<Txn> => {
  const url = '/txndata/getTxn'; // Define the URL with placeholders
  return fetchData<Txn>(url, [txnId]);
};

export const fetchLedger = async (ledgerId: string): Promise<Ledger> => {
  const url = '/ledger/getLedger'; // Define the URL with placeholders
  return fetchData<Ledger>(url, [ledgerId]);
};

function handleApiError(error: any): never {
  if (error.response) {
    // Extract error messages from the response
    const errorMessages = Array.isArray(error.response.data.message)
      ? error.response.data.message
      : [error.response.data.message];

    const formattedMessage = errorMessages.join('\n');
    console.error('API Error Response:', formattedMessage);
    throw new Error(formattedMessage);
  } else {

    console.error('Error occurred:', error.message);
    throw error; // Re-throw the original error for upstream handling
  }
}


export interface LedgerViewResponse {
  ledger: Ledger;
  renderLedger: string;
}

export const getLedgerView = async (ledgerId: string): Promise<LedgerViewResponse> => {
  const url = '/ledger/getLedgerView';
  return fetchData<LedgerViewResponse>(url, [ledgerId]);
};

export const upsertLedger = async (ledger: Ledger, file?: File) => {
  const url = '/ledger/upsertLedger';
  try {
    const formData = new FormData();
    formData.append('data', JSON.stringify(ledger));
    if (file) {
      formData.append('file', file);
    }

    const headers = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    return await postWithFile(url, formData, headers);
  } catch (error: any) {
    handleApiError(error)
  }
}

export const getTxnView = async (txnId: string, isPackingSlip?: boolean): Promise<string> => {
  const queryParams: Record<string, any> = {};
  const url = '/txndata/getTxnView'; // Define the URL with placeholders
  if (isPackingSlip) queryParams['isPackingSlip'] = isPackingSlip
  return fetchData<string>(url, [txnId], queryParams);
};

export interface TxnViewResponse {
  txn: any;
  renderTxn: string;
}

export interface PaymentViewResponse {
  payment: any;
  renderPayment: string;
}


export const getTxnPublicView = async (txnId: string, isPackingSlip?: boolean): Promise<TxnViewResponse> => {
  const url = `/txndata/getTxnPublicView/${txnId}`; // Adjust the URL to include the txnId parameter
  const queryParams: Record<string, any> = {};
  if (isPackingSlip) queryParams['isPackingSlip'] = isPackingSlip
  return fetchData<TxnViewResponse>(url, undefined, queryParams);
};

export const getPaymentPublicView = async (paymentId: string): Promise<PaymentViewResponse> => {
  const url = `/txndata/getPaymentPublicView/${paymentId}`; // Adjust the URL to include the txnId parameter
  return fetchData<PaymentViewResponse>(url);
};

export const getPaymentView = async (paymentId: string): Promise<string> => {
  const url = '/txndata/getPaymentView'; // Define the URL with placeholders
  return fetchData<string>(url, [paymentId]);
};


export const deleteTxnById = async (txnId: string): Promise<string | void> => {
  const url = `/txndata/deleteTxn/${txnId}`; // Construct the URL with the txnId
  try {
    const response = await deleteWithCacheUpdate<string>(url); // Assuming deleteWithCacheUpdate is your custom delete function
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};

export const deleteLedgerById = async (ledgerId: string): Promise<string | void> => {
  const url = `/ledger/deleteLedger/${ledgerId}`;
  try {
    const response = await deleteWithCacheUpdate<string>(url);
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};


export const deletePaymentById = async (paymentId: string): Promise<string | void> => {
  const url = `/txnData/deletePayment/${paymentId}`; // Construct the URL with the paymentId
  try {
    const response = await deleteWithCacheUpdate<string>(url);
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};

export const getContactBalance = async (contactId: string, dateFilter: DateFilter) => {
  try {
    const params: { [key: string]: any } = {
      dateFilter: JSON.stringify(dateFilter),
    };

    // Attempt to fetch data
    const response = await fetchData<ContactBalance>(
      `/masterdata/getContactBalance/${contactId}`,
      undefined,
      params
    );

    return response;
  } catch (error) {
    handleApiError(error);
  }
};

export const getContactBalanceNew = async (contactId: string, dateFilter: DateFilter) => {
  try {
    const params: { [key: string]: any } = {
      dateFilter: JSON.stringify(dateFilter),
    };

    // Attempt to fetch data
    const response = await fetchData<ContactBalance>(
      `/masterdata/getContactBalanceNew/${contactId}`,
      undefined,
      params
    );

    return response;
  } catch (error) {
    handleApiError(error);
  }
};

// MBAI-448: validate GSTIN format and fetch taxpayer details via backend (sandbox.co.in)
export const lookupGSTIN = async (gstin: string): Promise<any> => {
  return fetchDataWithCacheOption<any>(`/masterdata/lookupGSTIN/${encodeURIComponent(gstin)}`, true);
};

export const fetchContact = async (contactId: string): Promise<{ contact: Contact, files: File[] }> => {
  const contact = await fetchDataWithCacheOption<Contact>(`/masterdata/getContact/${contactId}`, true);
  const files: File[] = [];

  if (contact.contactImages && contact.contactImages.length > 0) {
    for (const image of contact.contactImages) {
      const encodedImageURL = encodeURIComponent(image);
      const fileBlob = await fetchFileWithCache(`/utils/getFileByKey/${encodedImageURL}`);
      files.push(fileBlob as unknown as File);
    }
  }
  return { contact, files };
};

export const upSertTxn = async (txn: Txn, file?: File) => {
  try {
    const formData = new FormData();
    formData.append('txnDto', JSON.stringify(txn));
    if (file) {
      formData.append('file', file);
    }

    const response = await postWithFile('/txndata/upsertTxn', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response;
  } catch (error) {
    handleApiError(error)
  }
};

export const upSertPayment = async (payment: Payment, file?: File) => {
  try {
    const formData = new FormData();
    formData.append('paymentDto', JSON.stringify(payment));
    if (file) {
      formData.append('file', file);
    }
    const response = await postWithFile('/txndata/upsertPayment', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response;
  } catch (error) {
    handleApiError(error)
  }
};

export const getPayment = async (paymentId: string) => {
  return fetchData<Payment>(`/txndata/getPayment/${paymentId}`)
}

export const getReturnPayment = async (returnTxnId: string) => {
  return fetchData<Payment>(`/txndata/getReturnPayment/${returnTxnId}`)
}

// Function to fetch and handle PDF data
export const getTxnPdf = async (txnId: string, isPackingSlip?: boolean) => {
  const queryParams: Record<string, any> = {};
  try {
    if (isPackingSlip) queryParams['isPackingSlip'] = isPackingSlip;
    const { data } = await fetchBlobData(`/txndata/getTxnPdf/${txnId}`, { params: queryParams, responseType: 'arraybuffer' });
    return new Blob([data], { type: 'application/pdf' });
  } catch (error) {
    console.error('Error fetching Transaction PDF:', error);
    throw error;
  }
};

// Function to fetch and handle PDF data
export const getPaymentPdf = async (paymentId: string) => {
  try {
    const { data } = await fetchBlobData(`/txndata/getPaymentPdf/${paymentId}`, { responseType: 'arraybuffer' });
    return new Blob([data], { type: 'application/pdf' });
  } catch (error) {
    console.error('Error fetching Payment PDF:', error);
    throw error;
  }
};



export const getLedgerPdf = async (ledgerId: string): Promise<Blob> => {
  try {
    const { data } = await fetchBlobData(`/ledger/getLedgerPdf/${ledgerId}`, { responseType: 'arraybuffer' });
    return new Blob([data], { type: 'application/pdf' });
  } catch (error) {
    console.error('Error fetching Ledger PDF:', error);
    throw error;
  }
};

export const downloadLedgerPdf = async (ledgerId: string, ledgerNo?: string): Promise<void> => {
  const pdfBlob = await getLedgerPdf(ledgerId);
  const url = window.URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${ledgerNo || ledgerId}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadPdf = async (txnId: string) => {
  try {
    const pdfBlob = await getTxnPdf(txnId);
    const url = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${txnId}.pdf`); // Specify the filename for download
    document.body.appendChild(link);
    link.click();
    link.remove(); // Cleanup
  } catch (error) {
    console.error('Error downloading PDF:', error);
  }
};

export const downloadSampleFile = async (
  uploadObject: string,
  subType?: string,
  country?: string
) => {
  try {
    // Build the URL with country parameter in the path
    const countryPath = country ? `/${encodeURIComponent(country.toUpperCase())}` : '';
    const queryParam = subType ? `?subType=${encodeURIComponent(subType)}` : '';

    const { data } = await fetchBlobData(`/utils/sampleFile/${uploadObject}${countryPath}${queryParam}`);
    const blob = data instanceof Blob ? data : new Blob([data]);

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;

    // Include country code in filename if provided
    let fileName: string;
    if (country && subType) {
      fileName = `${uploadObject}-${subType}-${country.toUpperCase()}.xlsx`;
    } else if (country) {
      fileName = `${uploadObject}-${country.toUpperCase()}.xlsx`;
    } else if (subType) {
      fileName = `${uploadObject}-${subType}.xlsx`;
    } else {
      fileName = `${uploadObject}.xlsx`;
    }

    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading file:', error);

    // Enhanced error handling with more context
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error(
          `Sample file not found${country ? ` for country ${country}` : ''}. Please contact support.`
        );
      }
      throw new Error(`Failed to download file: ${error.message}`);
    }

    throw new Error('Failed to download file. Please try again.');
  }
};




export const openPdfInNewTab = async (txnId: string, txnNo: string, isPackingSlip?: boolean) => {
  try {
    const pdfBlob = await getTxnPdf(txnId, isPackingSlip); // Assuming this returns a Blob

    if (!(pdfBlob instanceof Blob)) {
      throw new Error('Expected a Blob, but received a different type.');
    }

    // Generate a URL for the Blob
    const url = window.URL.createObjectURL(pdfBlob);

    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';

    // Set the filename with the transaction ID
    link.download = `${txnNo}.pdf`;

    // Append link to the document, trigger click, and remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoke the Blob URL to free up resources
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Error opening PDF in new tab:', error);
  }
};

export const openPaymentPdfInNewTab = async (paymentId: string, paymentNo: string) => {
  try {
    const pdfBlob = await getPaymentPdf(paymentId); // Assuming this returns a Blob

    if (!(pdfBlob instanceof Blob)) {
      throw new Error('Expected a Blob, but received a different type.');
    }

    // Generate a URL for the Blob
    const url = window.URL.createObjectURL(pdfBlob);

    // Create a temporary link element
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';

    // Set the filename with the transaction ID
    link.download = `${paymentNo}.pdf`;

    // Append link to the document, trigger click, and remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoke the Blob URL to free up resources
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Error opening PDF in new tab:', error);
  }
};



// Function to fetch and handle ledger entries
export const getLedgerEntriesWithBalance = async (
  reportFilter: ReportFilter
): Promise<Ledger[]> => {
  const params: { [key: string]: any } = {};
  params['reportFilter'] = JSON.stringify(reportFilter)
  return fetchData<Ledger[]>(APIEndpoints.GET_LEDGER_ENTRIES_WITH_BALANCE, undefined, params);
};

export interface InvoiceProfitabilityRow {
  txnId: string;
  txnNo: string;
  txnDate: string;
  contactId: string;
  contactName: string;
  revenue: number;
  cost: number;
  grossProfit: number;
  marginPct: number;
}

export const getInvoiceProfitability = async (
  reportFilter: ReportFilter
): Promise<InvoiceProfitabilityRow[]> => {
  const params: { [key: string]: any } = {};
  params['reportFilter'] = JSON.stringify(reportFilter);
  return fetchData<InvoiceProfitabilityRow[]>('/reporting/invoice-profitability', undefined, params);
};


// Function to fetch and handle ledger entries
export const getLedgerEntries = async (
  reportFilter: ReportFilter
): Promise<Ledger[]> => {
  const params: { [key: string]: any } = {};
  params['reportFilter'] = JSON.stringify(reportFilter)
  return fetchData<Ledger[]>(`/reporting/ledger-entries`, undefined, params);
};

export const getLedgerTotals = async (
  reportFilter: ReportFilter
): Promise<LedgerReport[]> => {
  const params: { [key: string]: any } = {};
  params['reportFilter'] = JSON.stringify(reportFilter)
  return fetchData<LedgerReport[]>(`/reporting/ledger-totals`, undefined, params);
};

export const getContactTxns = async ({
  predefinedDateFilter,
  fromDate,
  toDate,
  items,
  contacts,
  txnType,
  reportType,
}: {
  predefinedDateFilter: string,
  fromDate?: Date,
  toDate?: Date,
  items?: string[],
  contacts?: string[],
  txnType?: TxnType,
  reportType?: ReportQueryType
}): Promise<ContactTxnsForReport> => {
  // The backend takes one JSON-encoded `reportFilter` query param (same contract as contact-statement)
  const reportFilter: { [key: string]: any } = {
    dateFilter: { predefinedDateFilter, fromDate: fromDate?.toISOString(), toDate: toDate?.toISOString() },
    reportType: reportType ?? ReportQueryType.CombinedBase,
  };
  if (txnType) reportFilter.txnType = txnType;
  if (items && items.length > 0) reportFilter.itemFilter = items;
  if (contacts && contacts.length > 0) reportFilter.contactFilter = contacts;
  return fetchDataWithCacheOption<ContactTxnsForReport>('/reporting/contact-txns', true, undefined, { reportFilter: JSON.stringify(reportFilter) });
};

export const renderTemplate = async (templateConfig: TemplateConfig) => {
  const response = await postWithCacheClear('/appservice/templateConfig', templateConfig);
  return response;
};

export const getTemplateConfigHtml = async (templateConfig: TemplateConfig, part?: string,) => {
  const queryParams: Record<string, any> = {};
  if (part) queryParams['part'] = part

  const response = await postWithCacheClear('/appservice/updateViewForTemplateConfig', templateConfig,
    {
      params: queryParams // Attach query parameters here
    }
  );
  return response as string
};

export const fetchFile = async (fileKey: string): Promise<Blob | null> => {
  try {
    // const response = await axiosCache.get(`/utils/getFileByKey/${encodeURIComponent(fileKey)}`, { responseType: 'blob' });
    const fileBlob = await fetchFileWithCache(`/utils/getFileByKey/${encodeURIComponent(fileKey)}`);
    return fileBlob;
  } catch (err) {
    console.error('Failed to fetch file', err);
    return null;
  }
};

export const getNextNumber = async (object: string): Promise<string> => {
  return fetchDataWithCacheOption<string>(`/appservice/nextNumber/${object}`, true);
}

export const fetchFieldConfig = async (object: string): Promise<FieldConfig[]> => {
  const response = await fetchData<FieldConfig[]>(`/appservice/getFieldConfig`, [object]);
  const fieldConfigs: FieldConfig[] = response
  fieldConfigs.sort((a, b) => {
    const sortOrderA = a.sortOrder !== undefined ? a.sortOrder : Infinity;
    const sortOrderB = b.sortOrder !== undefined ? b.sortOrder : Infinity;
    return sortOrderA - sortOrderB;
  });
  return response;
}

export const upsertFieldConfig = async (fieldConfig: FieldConfig[]) => {
  const response = await postWithFile(`/appservice/upsertFieldConfig`, fieldConfig, undefined);
  return response;
}

export const getTableConfig = async (object: string, screen: string, subObject?: string): Promise<TableConfig[]> => {
  if (object) {
    return fetchData<TableConfig[]>(`/appservice/getTableConfig`, [object, screen, subObject || '']);
  } else {
    return []
  }
}

export const upsertTableConfig = async (tableConfig: TableConfig[]) => {
  const response = await postWithFile(`/appservice/upsertTableConfig`, tableConfig, undefined);
  return response;
}

export const getBankTxns = async (bankTxnQuery?: BankTransactionQueryOptions) => {
  const queryParams: { [key: string]: any } = {};
  queryParams['bankTxnQuery'] = JSON.stringify(bankTxnQuery)
  const pathParams = bankTxnQuery?.bankAcct ? [bankTxnQuery.bankAcct] : undefined;
  return fetchData<BankTransactions[]>(APIEndpoints.GET_BANKTXNS, pathParams, queryParams);
}

// MBAI-528 reconciliation board: the "In myBooksAi" column's data source.
export const fetchUnmatchedLedgerItems = async (bankAcct: string, dateFilter: DateFilter): Promise<UnmatchedLedgerItem[]> => {
  return fetchData<UnmatchedLedgerItem[]>(APIEndpoints.GET_UNMATCHED_LEDGER, [bankAcct], { dateFilter: JSON.stringify(dateFilter) });
}

export class ContactTxnsForReport {
  txnList: Txn[] = [];
  openReturnList: Txn[] = [];
  paymentList: Payment[] = [];
  openingBalanceList: Txn[] = [];
  expenseList: Txn[] = [];
  constructor(init?: Partial<ContactTxnsForReport>) {
    Object.assign(this, init);
  }
}


export const loginApi = async (login: LoginDto) => {
  const response = await postWithCacheClear('/auth/login', login);
  return response;
}

export const socialLogin = async (socialSignInDto: SocialSignInDto) => {
  try {
    const response = await postWithCacheClear('/auth/socialSignIn', socialSignInDto);
    return response;
  } catch (error: any) {
    // Check if the error is due to user not found
    if (error.response && error.response.status === 404) {
      console.warn('User not found');
      return null;
    }
    console.error('Error logging in:', error);
    return null;
  }
};

export const socialSignup = async (socialSignUpDto: SocialSignUpDto) => {
  try {
    const response = await postWithCacheClear('/auth/socialSignUp', socialSignUpDto);
    return response;
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      console.warn('User not found');
      return null;
    }
    console.error('Error logging in:', error);
    return null;
  }
};

export const getUserRoleAccess = async () => {
  return fetchData<RoleAccess>('/auth/userAccessRole')
}

export const fetchCustomRoles = async (): Promise<CustomRole[]> => {
  return fetchData<CustomRole[]>('/customroles/list');
};

export const upSertCustomRole = async (role: CustomRole): Promise<CustomRole> => {
  try {
    const response = await postWithCacheClear<CustomRole>('/customroles/upsert', role);
    lastUpdatesCacheTimestamp = 0;
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};

export const deleteCustomRole = async (roleId: string): Promise<void> => {
  try {
    const response = await postWithCacheClear('/customroles/delete', { roleId });
    lastUpdatesCacheTimestamp = 0;
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};

export const fetchAvailablePermissions = async (): Promise<AvailablePermissions> => {
  return fetchDataWithCacheOption<AvailablePermissions>('/customroles/availablePermissions', false);
};

// Direct axiosCache call is intentional — requires a per-call Authorization header override
// that the fetchData wrappers do not support (they rely on the interceptor-injected token)
export const verifyToken = async (token: string) => {
  const response = await axiosCache.get('/auth/verifyToken', {
    headers: { Authorization: `Bearer ${token}` },
    cache: false
  });
  return response.data;
};

export const validateEmail = async (email: string) => {
  try {
    const response = await postWithCacheClear('/auth/validateEmail', { email: email });
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
}

export const validateOTP = async (otpInfo: OTPInfo) => {
  const response = await postWithCacheClear('/auth/validateOTP', otpInfo);
  return response;
}

export const signup = async (signupDTO: SignupDTO) => {
  const response = await postWithCacheClear('/auth/signup', signupDTO);
  return response;
}

export const createConversation = async (): Promise<{ id: string }> => {
  return postWithCacheClear('/ai/conversations', {});
}

export const sendChatMessage = async (conversationId: string, message: string): Promise<string> => {
  const response = await postWithCacheClear(`/ai/conversations/${conversationId}/chat`, { message });
  return response.content;
}

export const getConversationMessages = async (conversationId: string) => {
  return fetchDataWithCacheOption(`/ai/conversations/${conversationId}/messages`, true);
}

export const getStatusBrief = async (): Promise<{ summary: string | null; positives: string[]; improvements: string[]; generatedAt: string | null; fromCache: boolean; reason?: 'insufficient_data' | 'data_unavailable'; invoiceCount?: number; requiredInvoiceCount?: number }> => {
  return fetchDataWithCacheOption('/ai/status-brief', true);
};

export const getReportInsight = async (
  reportType: string,
  period: { from: string; to: string; label: string },
  data: Record<string, any>
): Promise<{ insight: string | null; generatedAt: string | null; fromCache: boolean }> => {
  return postWithCacheClear('/ai/report-insights', { reportType, period, data });
};

export const scanInvoice = async (file: File, txnType: string): Promise<{ txnHeader: Partial<TxnHeader>; lineItems: Partial<LineItem>[]; fileUrl: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('txnType', txnType);
  return postWithCacheClear('/ai/scan-invoice', formData, { 'Content-Type': 'multipart/form-data' });
};

// Rule + auto-match suggestions for an account's pending lines, keyed by bankTxnId. Split out of
// getBankTxns (skipSuggestions) because computing them is the slow part of opening the screen.
export const getBankSuggestions = async (bankAcct: string): Promise<Record<string, BankLineSuggestions>> => {
  return (await fetchData<Record<string, BankLineSuggestions>>(APIEndpoints.GET_BANK_SUGGESTIONS, [bankAcct])) || {};
};

export const getBankAiUsage = async (): Promise<AiUsage> => {
  return fetchDataWithCacheOption<AiUsage>('/banking/ai-suggest/usage', true);
};

export const getBankAiSuggestions = async (
  bankAccountId: string,
  lines: { bankTxnId: string; description: string; amount: number; date: string }[]
): Promise<{ suggestions: any[]; failedBatches?: number; usage?: AiUsage }> => {
  return postWithCacheClear('/banking/ai-suggest', { bankAccountId, lines });
};

// ── Opening-balance sync ─────────────────────────────────────────────────────
// A Plaid-linked account anchors on the bank's live balance (server side); any other account uses
// the running balances stored from its uploaded statement, or a balance + date the user types in.
export interface OpeningBalanceInput { balance?: number; asOf?: string }

export const getOpeningBalancePreview = async (
  bankAcct: string,
  isPlaidLinked: boolean,
  input?: OpeningBalanceInput,
): Promise<OpeningBalancePreview> => {
  if (isPlaidLinked) {
    return fetchDataWithCacheOption<OpeningBalancePreview>(`/plaid/opening-balance/${encodeURIComponent(bankAcct)}`, true);
  }
  const query = input?.balance != null && input.asOf ? { balance: input.balance, asOf: input.asOf } : undefined;
  return fetchDataWithCacheOption<OpeningBalancePreview>(`/banking/opening-balance/${encodeURIComponent(bankAcct)}`, true, undefined, query);
};

export const applyOpeningBalance = async (
  bankAcct: string,
  isPlaidLinked: boolean,
  input?: OpeningBalanceInput,
): Promise<OpeningBalancePreview> => {
  const result = isPlaidLinked
    ? await postWithCacheClear<OpeningBalancePreview>(`/plaid/opening-balance/${encodeURIComponent(bankAcct)}`, {})
    : await postWithCacheClear<OpeningBalancePreview>(`/banking/opening-balance/${encodeURIComponent(bankAcct)}`, input ?? {});
  // The books' starting point moved: every balance and every bank-line list derived from it is stale.
  forgetCacheTimestamps([APIEndpoints.GET_BANKDETAILS, APIEndpoints.GET_ACCOUNTS, ...BANK_LIST_URLS]);
  return result;
};

// Whether a bank feed (Plaid) is connected to this account. Editions without Plaid get a 403 — that is
// just "no".
export const isPlaidLinkedAccount = async (bankAcct: string): Promise<boolean> => {
  try {
    const items = await fetchPlaidItems();
    return items.some(({ item, accounts }) => item.status !== 'revoked' && accounts.some(a => a.isLinked && a.glAccountId === bankAcct));
  } catch {
    return false;
  }
};

export const dismissBankAiSuggestion = async (bankTxnId: string): Promise<void> => {
  await postWithCacheClear('/banking/ai-suggest/dismiss', { bankTxnId });
};

// MBAI-528: Automatch Rules ────────────────────────────────────────────────

export const createBankRule = async (dto: CreateBankRuleDto): Promise<BankRule> => {
  const rule = await postWithCacheClear('/banking/rules', dto);
  forgetBankTxnListCacheTimestamps();
  return rule;
};

export const fetchBankRules = async (bankAcct?: string): Promise<BankRule[]> => {
  return fetchData<BankRule[]>('/banking/rules', undefined, bankAcct ? { bankAcct } : undefined);
};

export const previewBankRule = async (bankAcct: string | undefined, conditions: CreateBankRuleDto['conditions']): Promise<RulePreviewResult> => {
  return postWithCacheClear('/banking/rules/preview', { bankAcct, conditions });
};

export const updateBankRule = async (ruleId: string, dto: UpdateBankRuleDto): Promise<BankRule> => {
  const rule = await patchWithCacheClear(`/banking/rules/${ruleId}`, dto);
  forgetBankTxnListCacheTimestamps();
  return rule;
};

export const deleteBankRule = async (ruleId: string): Promise<{ success: boolean }> => {
  const result = await deleteWithCacheUpdate(`/banking/rules/${ruleId}`);
  forgetBankTxnListCacheTimestamps();
  return result as { success: boolean };
};

export const backfillBankRule = async (ruleId: string): Promise<RulePreviewResult> => {
  const result = await postWithCacheClear(`/banking/rules/${ruleId}/backfill`, {});
  forgetBankTxnListCacheTimestamps();
  return result;
};

export const fetchBranches = async () => {
  return fetchData<BranchDetails[]>(`/appservice/getBranchesForUser`);
}

export const switchBranch = async (branchId: string) => {
  const response = await postWithCacheClear('/auth/switchBranch', { branchId: branchId });
  return response;
}

export const switchBiz = async (bizId: string, branchId?: string) => {
  try {
    const response = await postWithCacheClear('/auth/switchBiz', { bizId: bizId, branchId: branchId }, undefined)
    return response
  } catch (error) {
    handleApiError(error)
  }
}

export const doLogout = async () => {
  // await axiosCache.storage.remove('/reporting/getDashboard')
}

export const blobToDataURL = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Caches the business logo as a data URL on globalValue so PDF export headers can
// draw it via jsPDF's addImage without an async round trip.
export const cacheBizLogo = async (logoBlob: Blob | undefined): Promise<void> => {
  if (!logoBlob) {
    globalValue.bizLogoDataUrl = undefined;
    return;
  }
  try {
    globalValue.bizLogoDataUrl = await blobToDataURL(logoBlob);
  } catch {
    globalValue.bizLogoDataUrl = undefined;
  }
};

export const fetchBizMaster = async (): Promise<{ bizMaster: BizMaster, logo?: Blob, seal?: Blob, sign?: Blob }> => {
  try {
    const response = await fetchData<BizMaster>('/appservice/getBizMaster');

    if (response) {
      const bizMaster = response;
      try {
        const fetchFile = async (fileKey: string): Promise<Blob | undefined> => {
          if (fileKey) {
            const encodedImageURL = encodeURIComponent(fileKey);
            const fileResponse = await fetchFileWithCache(`/utils/getFileByKey/${encodedImageURL}`)
            return fileResponse as Blob;
          }
        };
        const logo = await fetchFile(bizMaster?.bizDetails?.logo || '');
        const sign = await fetchFile(bizMaster?.bizDetails?.sign || '');
        const seal = await fetchFile(bizMaster?.bizDetails?.seal || '');
        return { bizMaster, logo, sign, seal };
      } catch (error) {
        return { bizMaster }
        console.error('Error Getting File' + error)
      }



    } else {
      throw new Error(`Unexpected response status`);
    }
  } catch (error) {
    console.error('Error getting BizMaster:', error);
    throw error;
  }
};

const createCustomFilename = (file: File, customName: string): string => {
  const extension = file.name.split('.').pop();  // Extract the file extension
  return `${customName}.${extension}`;  // Return the custom name with the original file's extension
};

// Replace the existing upsertBizMaster function
export const upsertBizMaster = async (
  bizDetails: BizDetails,
  logo?: File,
  seal?: File,
  sign?: File,
  logoDeleted?: boolean,
  sealDeleted?: boolean,
  signDeleted?: boolean
) => {
  const formData = new FormData();

  // Append the contact object as a JSON string
  formData.append('bizDetails', JSON.stringify(bizDetails));

  // Append deletion flags
  if (logoDeleted) {
    formData.append('logoDeleted', 'true');
  }
  if (sealDeleted) {
    formData.append('sealDeleted', 'true');
  }
  if (signDeleted) {
    formData.append('signDeleted', 'true');
  }

  // Append the files if they exist
  if (logo) {
    formData.append('files', logo, createCustomFilename(logo, 'logo'));
  }

  if (seal) {
    formData.append('files', seal, createCustomFilename(seal, 'seal'));
  }

  if (sign) {
    formData.append('files', sign, createCustomFilename(sign, 'sign'));
  }

  // Make the request using formData
  const response = await postWithFile('/appservice/upsertBizmaster', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
}


// Function to handle file upload
export const uploadRecords = async (file: File, uploadOptions: any) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('uploadOptions', JSON.stringify(uploadOptions));
  const response = await postWithFile('/utils/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  });
  return response;
};


export const fetchAppLogs = async (appLogFilter: AppLogFilter): Promise<AppLog[] | undefined> => {
  try {
    const queryParams: { [key: string]: any } = {};
    if (appLogFilter) {
      // Format the date as a string (e.g., YYYY-MM-DD)
      queryParams['appLogFilter'] = JSON.stringify(appLogFilter)
    }
    const response = fetchData<AppLog[]>('/utils/readAppLogs', undefined, queryParams)
    return response
  } catch (error) {
    console.error('Error fetching AppLogs:', error);
    throw error;
  }
}

export const fetchUploadHistory = async (params: {
  fromDate?: string;
  toDate?: string;
  logSubType?: string[];
  page?: number;
  limit?: number;
}): Promise<UploadHistoryResponse> => {
  const queryParams: Record<string, any> = {};
  if (params.fromDate) queryParams['fromDate'] = params.fromDate;
  if (params.toDate) queryParams['toDate'] = params.toDate;
  if (params.logSubType?.length) queryParams['logSubType'] = params.logSubType.join(',');
  if (params.page) queryParams['page'] = params.page;
  if (params.limit) queryParams['limit'] = params.limit;
  return fetchData<UploadHistoryResponse>('/utils/uploadHistory', undefined, queryParams);
};

export const getTxnLogs = async (objectId: string): Promise<AppLog[]> => {
  return fetchData<AppLog[]>(`/txndata/getTxnLogs/${objectId}`);
};

export const fetchAuditReport = async (params: {
  fromDate?: Date;
  toDate?: Date;
  entityType?: string[];
  action?: string[];
  userId?: string[];
  page?: number;
  limit?: number;
}): Promise<AuditReportResponse> => {
  const queryParams: Record<string, any> = {};
  if (params.fromDate) queryParams['fromDate'] = params.fromDate;
  if (params.toDate) queryParams['toDate'] = params.toDate;
  if (params.entityType?.length) queryParams['entityType'] = params.entityType.join(',');
  if (params.action?.length) queryParams['action'] = params.action.join(',');
  if (params.userId?.length) queryParams['userId'] = params.userId.join(',');
  if (params.page) queryParams['page'] = params.page;
  if (params.limit) queryParams['limit'] = params.limit;
  return fetchData<AuditReportResponse>('/utils/auditReport', undefined, queryParams);
};

export const fetchEntityAuditLog = async (entityId: string): Promise<AppLog[]> => {
  return fetchData<AppLog[]>(`/txndata/getTxnLogs/${entityId}`);
};

// Function to fetch FxRates
export const fetchFxRates = async (currId: string, date?: Date): Promise<number | undefined> => {
  const queryParams: { [key: string]: any } = {};
  if (date) {
    queryParams['date'] = date;
  }
  return fetchData<number>(
    '/masterdata/getFxRate',
    [currId],
    queryParams,
  );
};

export const upsertBankTransaction = async (bankTransaction: BankTransactions) => {
  const response = await postWithCacheClear('banking/upsertBankTransaction', bankTransaction);
  return response;
}

export const undoBankTransaction = async (bankTransaction: BankTransactions[]) => {
  try {
    const response = await postWithCacheClear('banking/undoBankTransaction', bankTransaction)
    return response;
  } catch (error) {
    handleApiError(error)
  }
}

export const bulkCategorize = async (
  bulkCategoriseParams: BulkCategoriseParams,
  bankTransactions: BankTransactions[]
) => {
  const payload = {
    bulkCategoriseParams,
    bankTransactions
  };
  const response = await postWithCacheClear('banking/bulk-categorize', payload);
  return response;
};

export const deleteBankTransaction = async (bankTransaction: BankTransactions[]) => {
  const response = await postWithCacheClear('banking/deleteBankTransactions', bankTransaction);
  return response;
}

export const fetchBizSettings = async (): Promise<BizSettings> => {
  return fetchData<BizSettings>('/appservice/getBizSettings');
};

// ── Customer Portal Settings ──────────────────────────────────────────────────

export interface PortalSettingsDto {
  portalEnabled?: boolean;
  bizSlug?: string;
  primaryColor?: string;
  showShop?: boolean;
  // Server-derived (from PORTAL_BASE_URL) — read-only, used to preview the full portal
  // link as the admin types a slug. Ignored by the backend if sent back on save.
  portalBaseUrl?: string;
}

export interface PortalProfileSettingsDto {
  profileVisible?: boolean;
  description?: string;
  tagline?: string;
  category?: string;
  businessHours?: string;
  contactEmail?: string;
}

export const fetchPortalSettings = async (): Promise<PortalSettingsDto> => {
  return fetchData<PortalSettingsDto>('/portal/settings');
};

export const upsertPortalSettings = async (dto: PortalSettingsDto): Promise<PortalSettingsDto> => {
  return putWithCacheClear<PortalSettingsDto>('/portal/settings', dto);
};

export interface SlugAvailability {
  available: boolean;
  slug: string;
}

export const checkPortalSlugAvailability = async (slug: string): Promise<SlugAvailability> => {
  return fetchData<SlugAvailability>('/portal/settings/slug-availability', undefined, { slug });
};

export const fetchPortalProfileSettings = async (): Promise<PortalProfileSettingsDto> => {
  return fetchData<PortalProfileSettingsDto>('/portal/settings/profile');
};

export const upsertPortalProfileSettings = async (dto: PortalProfileSettingsDto): Promise<PortalProfileSettingsDto> => {
  return putWithCacheClear<PortalProfileSettingsDto>('/portal/settings/profile', dto);
};

export interface SendBusinessSummaryResponse {
  sent: boolean;
  to: string;
  period: string;
  kpis: {
    sales: number;
    expenses: number;
    netProfit: number;
    profitMarginPct: number;
  };
  invoicesProcessed: number;
  topCustomersCount: number;
  topProductsCount: number;
  expenseCategoriesCount: number;
}

export const sendBusinessSummaryEmail = async (
  period?: string,
  email?: string,
): Promise<SendBusinessSummaryResponse> => {
  const body: Record<string, string> = {};
  if (period) body.period = period;
  if (email)  body.email  = email;
  const response = await postWithFile('/reporting/send-business-summary', body, undefined);
  return response;
};

export const upsertKVPair = async (kvPair: KVPairs): Promise<KVPairs> => {
  const response = await postWithCacheClear('appservice/upsertKVPair', kvPair);
  return response;
};

export const getKVPair = async (key: string, object: string | undefined): Promise<KVPairs | null> => {
  return fetchData<KVPairs | null>(`appservice/getKVPair/${key}/${object || ''}`);
};

export const fetchTemplateConfigsSummary = async (): Promise<{ variant: string; object?: string; isDefault: boolean }[]> =>
  fetchData<{ variant: string; object?: string; isDefault: boolean }[]>('/appservice/templateConfigsSummary');

export const applyTemplateToOverrides = async (templateConfig: TemplateConfig) => {
  const response = await postWithCacheClear('/appservice/applyTemplateToOverrides', templateConfig);
  return response;
};

export const getTemplateConfig = async (object?: string, template?: string): Promise<TemplateConfig | undefined> => {
  return fetchData<TemplateConfig>('/appservice/getTemplateConfig', [template || '', object || '']);
};

export const upsertTemplateConfig = async (templateConfig: TemplateConfig) => {
  const response = await postWithCacheClear('/appservice/upsertTemplateConfig', templateConfig);
  return response;
};

export const getCustomFieldOptions = async (txnType?: string): Promise<{
  biz: { key: string; label: string }[];
  contact: { key: string; label: string }[];
  item: { key: string; label: string }[];
  txn: { key: string; label: string }[];
}> => {
  const toKeyLabel = (configs: FieldConfig[]) =>
    configs
      .filter(c => !c.isStandard && !c.isDeleted && c.fieldName)
      .map(c => ({ key: c.fieldName!, label: c.fieldHeading || c.fieldName! }));

  const [bizConfigs, contactConfigs, itemConfigs, txnConfigs] = await Promise.all([
    fetchFieldConfig('BusinessProfile').catch(() => [] as FieldConfig[]),
    fetchFieldConfig('Contact').catch(() => [] as FieldConfig[]),
    fetchFieldConfig('Item').catch(() => [] as FieldConfig[]),
    txnType ? fetchFieldConfig(txnType).catch(() => [] as FieldConfig[]) : Promise.resolve([] as FieldConfig[]),
  ]);

  return {
    biz: toKeyLabel(bizConfigs),
    contact: toKeyLabel(contactConfigs),
    item: toKeyLabel(itemConfigs),
    txn: toKeyLabel(txnConfigs),
  };
};

export const fetchDashboardV2 = async (reportFilter: ReportFilter, silent?: boolean): Promise<DashboardResponseV2 | undefined> => {
  const queryParams: { [key: string]: any } = {};
  if (reportFilter) queryParams['reportFilter'] = JSON.stringify(reportFilter);
  return fetchData<DashboardResponseV2>('/reporting/getDashboard', undefined, queryParams, silent);
};

// Split out of fetchDashboardV2 (MBAI dashboard timeout, 2026-08-30) — the single getDashboard
// call fires 3 concurrent full aggregation passes server-side that contend with each other and
// exceed API Gateway's 29s hard timeout on data-heavy businesses. Each of these three instead
// calls its own backend endpoint doing a single aggregation pass, same shape as a P&L/Balance
// Sheet report. Callers MUST await these sequentially, not via Promise.all — firing all three
// concurrently was measured to still push getDashboardBalances past the 29s limit even against
// the deployed Lambda (separate invocations still contend on the shared MongoDB cluster).
// See project-docs/dashboard-performance-tuning.md in mybooks-backend for the full investigation.
export const fetchDashboardCurrentPeriod = async (reportFilter: ReportFilter, silent?: boolean): Promise<PeriodData | undefined> => {
  const queryParams: { [key: string]: any } = {};
  if (reportFilter) queryParams['reportFilter'] = JSON.stringify(reportFilter);
  return fetchData<PeriodData>('/reporting/getDashboardCurrentPeriod', undefined, queryParams, silent);
};

export const fetchDashboardPriorPeriod = async (reportFilter: ReportFilter, silent?: boolean): Promise<PeriodData | undefined> => {
  const queryParams: { [key: string]: any } = {};
  if (reportFilter) queryParams['reportFilter'] = JSON.stringify(reportFilter);
  return fetchData<PeriodData>('/reporting/getDashboardPriorPeriod', undefined, queryParams, silent);
};

export const fetchDashboardBalances = async (reportFilter: ReportFilter, silent?: boolean): Promise<BalancesDTO | undefined> => {
  const queryParams: { [key: string]: any } = {};
  if (reportFilter) queryParams['reportFilter'] = JSON.stringify(reportFilter);
  return fetchData<BalancesDTO>('/reporting/getDashboardBalances', undefined, queryParams, silent);
};

// MBAI-430: Needs Attention widget data (overdue/due-soon invoices + due-soon bills +
// uncategorized bank transactions), computed server-side. Not period-scoped like the
// three above — it's always "as of now", so no reportFilter param.
export const fetchDashboardNeedsAttention = async (silent?: boolean): Promise<DashboardNeedsAttentionDTO | undefined> => {
  return fetchData<DashboardNeedsAttentionDTO>('/reporting/getDashboardNeedsAttention', undefined, undefined, silent);
};

export const fetchDashboard = async (reportFilter: ReportFilter): Promise<DashboardResponse | undefined> => {
  const queryParams: { [key: string]: any } = {};
  if (reportFilter) {
    queryParams['reportFilter'] = JSON.stringify(reportFilter)
  }
  return fetchData<DashboardResponse>('/reporting/getDashboard', undefined, queryParams);
};

export const fetchBizListForUser = async (forceRefresh?: boolean): Promise<BizList[]> => {
  try {
    // forceRefresh skips the client cache — used when an earlier fetch (e.g. right after login) came back empty/failed.
    const response = forceRefresh
      ? await fetchDataWithCacheOption<BizList[]>(APIEndpoints.GET_BIZLIST_FOR_USER, true)
      : await fetchData<BizList[]>(APIEndpoints.GET_BIZLIST_FOR_USER)
    return response
  } catch (error) {
    handleApiError(error)
  }
}

export const createBizForUser = async (createBizDto: CreateBizDTO) => {
  try {
    const response = await postWithFile('/appservice/createBizForUser', createBizDto, undefined)
    return response
  } catch (error) {
    handleApiError(error)
  }
}

export const createBranch = async (createBranchDto: BranchDetails) => {
  try {
    const response = await postWithFile('/appservice/upsertBranch', createBranchDto, undefined)
    return response
  } catch (error) {
    handleApiError(error)
  }
}

// The balances are a live ledger computation that changes whenever any expense, payment or journal
// is added, edited or deleted (from any screen, tab or device). `fresh` skips the local cache so
// reconciliation screens never show a stale "myBooks balance" / "Difference" (MBAI-534).
export const fetchBankDetail = async (fresh = false): Promise<BankDetail[] | undefined> => {
  if (fresh) return fetchDataWithCacheOption<BankDetail[]>('/banking/getBankDetails', true);
  return fetchData<BankDetail[]>('/banking/getBankDetails');
};

// ── Plaid Bank Connections ────────────────────────────────────────────────────

export interface PlaidAccountDto {
  plaidAccountId: string;
  name: string;
  mask?: string;
  subtype: string;
  type?: string;
  glAccountId?: string;
  isLinked: boolean;
  currentBalance?: number;
  balanceUpdatedAt?: Date;
  hasExistingLedgerActivity?: boolean;
  earliestAllowedSyncDate?: Date;
  balanceMismatch?: boolean;
  balanceMismatchAmt?: number;
  balanceMismatchDetectedAt?: Date;
}

export interface BalanceMismatchSummary {
  plaidAccountId: string;
  glAccountId: string;
  accountName: string;
  plaidBalance: number;
  bookBalance: number;
  diff: number;
  suggestion: string;
  suggestionLinks: { label: string; path: string }[];
}

export interface PlaidItemDto {
  itemId: string;
  institutionId: string;
  institutionName: string;
  status: 'active' | 'error' | 'pending_expiration' | 'revoked';
  lastSyncedAt?: Date;
  initialFetchStartDate?: Date;
  initialFetchCompletedAt?: Date;
  errorCode?: string;
  removedReason?: string;
}

export interface PlaidItemWithAccounts {
  item: PlaidItemDto;
  accounts: PlaidAccountDto[];
}

export interface PlaidAccountMappingEntry {
  plaidAccountId: string;
  glAccountId?: string;
  createNew?: { accountName: string };
}

export const createPlaidLinkToken = async (): Promise<{ linkToken: string }> => {
  return postWithCacheClear('/plaid/link-token', {});
};

export const exchangePlaidPublicToken = async (
  publicToken: string,
  metadata?: Record<string, any>,
): Promise<{ itemId: string; accounts: PlaidAccountDto[] }> => {
  return postWithCacheClear('/plaid/exchange-public-token', { publicToken, metadata });
};

export const fetchPlaidItems = async (): Promise<PlaidItemWithAccounts[]> => {
  return fetchData<PlaidItemWithAccounts[]>('/plaid/items');
};

export const mapPlaidAccounts = async (mappings: PlaidAccountMappingEntry[]): Promise<PlaidAccountDto[]> => {
  return postWithCacheClear('/plaid/accounts/map', { mappings });
};

export const plaidHistoricalFetch = async (
  itemId: string,
  options: { startDate?: string; rangeDays?: 30 | 60 | 90 },
): Promise<{ fetched: number; mismatches: BalanceMismatchSummary[] }> => {
  return postWithCacheClear(`/plaid/items/${itemId}/historical-fetch`, options);
};

export const plaidManualSync = async (itemId: string): Promise<{ fetched: number; mismatches: BalanceMismatchSummary[] }> => {
  return postWithCacheClear(`/plaid/items/${itemId}/sync`, {});
};

export const disconnectPlaidItem = async (itemId: string): Promise<{ success: boolean }> => {
  return deleteData(`/plaid/items/${itemId}`);
};


export const fetchUsersForBiz = async (): Promise<User[] | undefined> => {
  return fetchData<User[]>('/auth/getUsersForBiz');
};

export const updateUser = async (user: Partial<User>): Promise<User> => {
  const response = await putWithCacheUpdate<User>(`/auth/updateUser/${user.userId}`, user);
  if (!response) {
    throw new Error('Failed to update user, no response from server.');
  }
  return response;
};

export const addNewUser = async (user: Partial<User>): Promise<User | { status: 'pending'; userId: string; email: string }> => {
  const response = await postWithFile(`/auth/addNewUser`, user, undefined);
  return response;
};

export const fetchPendingInvites = async (): Promise<PendingInvite[]> => {
  return fetchData<PendingInvite[]>('/auth/pending-invites');
};

export const acceptPendingInvite = async (bizId: string): Promise<{ message: string }> => {
  return postWithCacheClear(`/auth/pending-invites/${encodeURIComponent(bizId)}/accept`, {});
};

export const declinePendingInvite = async (bizId: string): Promise<{ message: string }> => {
  return postWithCacheClear(`/auth/pending-invites/${encodeURIComponent(bizId)}/decline`, {});
};

export const sendTxnApi = async (sendEmailDto: SendEmailDto) => {
  const response = await postWithFile(`/txndata/sendTxn`, sendEmailDto, undefined);
  return response;
};

export const sendTxnWp = async (sendTxnWPDto: SendTxnWPDto) => {
  const response = await postWithFile(`/txndata/sendTxnWP`, sendTxnWPDto, undefined);
  return response;
};

export const sendPaymentApi = async (sendEmailDto: SendEmailDto) => {
  const response = await postWithFile(`/txndata/sendPayment`, sendEmailDto, undefined);
  return response;
};

export const deleteContact = async (contactId: string) => {
  try {
    const response = await deleteWithCacheUpdate<string>(`/masterdata/deleteContact/${contactId}`);
    return response; // Expecting 'success' if the operation is successful
  } catch (error: any) {
    handleApiError(error)
  }
};

export const deleteUser = async (userId: string) => {
  try {
    const response = await deleteWithCacheUpdate<string>(`/auth/deleteUser/${userId}`);
    return response; // Expecting 'success' if the operation is successful
  } catch (error: any) {
    handleApiError(error)
  }
};

export const deleteItem = async (itemId: string) => {
  try {
    const response = await deleteWithCacheUpdate<string>(`/masterdata/deleteItem/${itemId}`);
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};

export const deleteItems = async (itemIds: string[]) => {
  try {
    // Make the API call to delete items
    const response = await postWithFile(`/masterdata/deleteItems`, itemIds, undefined);
    // If the operation is successful, return the response data
    return response;
  } catch (error: any) {
    // Capture and handle any errors
    handleApiError(error)
  }
};

export const deleteContacts = async (contactIds: string[]) => {
  try {
    // Make the API call to delete items
    const response = await postWithFile(`/masterdata/deleteContacts`, contactIds, undefined);
    // Return the full response so callers can inspect errors array
    return response;
  } catch (error: any) {
    // Capture and handle any errors
    handleApiError(error)
  }
};


export const deletePaymentTerm = async (paymentTermId: string) => {
  try {
    const response = await deleteWithCacheUpdate<string>(`masterdata/deletePaymentTerm/${paymentTermId}`);
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};

export const deleteUom = async (uomId: string) => {
  try {
    const response = await deleteWithCacheUpdate<string>(`/masterdata/deleteUom/${uomId}`);
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};

export const deleteZAttribute = async (attrId: string, attrType: string) => {
  try {
    const response = await deleteWithCacheUpdate<string>(`/masterdata/deleteZAttribute/${attrType}/${attrId}`);
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};


export const upsertPriceList = async (priceList: PriceList): Promise<PriceList> => {
  try {
    const response = await postWithFile('/masterdata/upsertPriceList', priceList, undefined);
    return response;
  } catch (error) {
    handleApiError(error)
  }
};

// Fetch all PriceLists
export const fetchPriceLists = async (): Promise<PriceList[]> => {
  return fetchData<PriceList[]>('/masterdata/getPriceLists');
};



export const deletePriceListById = async (pricingId: string): Promise<string | void> => {
  const url = `/masterdata/deletePriceList/${pricingId}`;
  try {
    const response = await deleteWithCacheUpdate<string>(url);
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};

export const deleteUserById = async (userId: string): Promise<string | void> => {
  const url = `/auth/deleteUser/${userId}`;
  try {
    const response = await deleteWithCacheUpdate<string>(url);
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};


// Fetch a single PriceList by pricingId
export const fetchPriceListById = async (pricingId: string): Promise<PriceList> => {
  return fetchData<PriceList>(`/masterdata/getPriceList/${encodeURIComponent(pricingId)}`);
};

// Uses a plain axios instance — not the backend instance — to avoid sending auth headers to an external service
const ipInfoToken = import.meta.env.VITE_APP_IPINFO_TOKEN || '60d17aa5abad6b';
export const fetchIpData = async () => {
  try {
    const response = await axios.get(`https://ipinfo.io/json?token=${ipInfoToken}`);
    return response.data;
  } catch (err) {
    console.error("Error fetching country code:", err);
  }
};


// Create a new API key
export const createApiKey = async (data: CreateApiKeyRequest): Promise<ApiKeyResponse> => {
  const response = await postWithFile('/appservice/createApiKey', data, undefined);
  return response;
};

// List all API keys for the user
export const listApiKeys = async (): Promise<ApiKey[]> => {
  try {
    const response = await fetchData<any>('/appservice/listApiKey');

    if (!response) return [];

    if (Array.isArray(response)) return response;

    if (response.apiKeys && Array.isArray(response.apiKeys)) return response.apiKeys;
    if (response && Array.isArray(response)) return response;

    return [];
  } catch (error) {
    console.error('Error fetching API keys:', error);
    return [];
  }
};


// Update an existing API key
export const updateApiKey = async (data: UpdateApiKeyRequest): Promise<ApiKey> => {
  try {
    const response = await patchData<ApiKey>('/appservice/updateApiKey', data);
    return response;
  } catch (error) {
    console.error('Error updating API key:', error);
    throw error;
  }
};

// Delete an API key
export const deleteApiKey = async (keyId: string): Promise<void> => {
  try {
    const response = await deleteData<void>('/appservice/deleteApiKey', undefined, { keyId });
    return response;
  } catch (error) {
    console.error('Error deleting API key:', error);
    throw error;
  }
};

export const reprocessBalances = async (startYear: number) => {
  const response = await postWithCacheClear(`/appservice/reprocessBalances?startYear=${startYear}`, {});
  return response;
};

export const findOverAllocations = async (): Promise<{
  overAllocatedInvoices: {
    txnId: string;
    txnNo: string;
    txnDate: string;
    txnType: string;
    contactName: string;
    totalAmt: number;
    paymentAmt: number;
    withHoldingAmt: number;
    totalAllocAmt: number;
    overAllocAmt: number;
    relatedPayments: {
      paymentId: string;
      paymentNo: string;
      paymentDate: string;
      paymentTotalAmt: number;
      allocAmt: number;
    }[];
  }[];
  overAllocatedPayments: {
    paymentId: string;
    paymentNo: string;
    paymentDate: string;
    contactName: string;
    paymentTotalAmt: number;
    sumOfLineAllocAmt: number;
    overAllocAmt: number;
    lines: {
      txnId: string;
      txnNo: string;
      allocAmt: number;
    }[];
  }[];
}> => {
  const response = await fetchDataWithCacheOption('/txndata/findOverAllocations', true);
  return response as any;
};

export const deleteBusiness = async (deleteData: { deleteCode: string; feedback?: string }) => {
  try {
    const response = await deleteWithCacheUpdate<any>('/appservice/deleteBusiness', deleteData);
    return response; // Expecting success response with deletion confirmation
  } catch (error: any) {
    handleApiError(error);
  }
};

export const requestDeleteConfirmation = async (): Promise<DeleteConfirmationResponse> => {
  try {
    const response = await postWithFile('/appservice/requestDeleteConfirmation', {}, undefined);
    return response;
  } catch (error) {
    handleApiError(error);
  }
};

export const requestDeleteBranchConfirmation = async (): Promise<DeleteConfirmationResponse> => {
  try {
    const response = await postWithFile('/appservice/requestDeleteBranchConfirmation', {}, undefined);
    return response;
  } catch (error) {
    handleApiError(error);
  }
};

export const deleteBranch = async (data: { deleteBranchCode: string }): Promise<any> => {
  const response = await deleteWithCacheUpdate<any>('/appservice/deleteBranch', data);
  return response;
};


export const getOnboardingProgress = async (): Promise<OnboardingProgress> => {
  return fetchData<OnboardingProgress>('/appservice/getOnboardingProgress');
};

export const updateOnboardingProgress = async (progress: Partial<OnboardingProgress>): Promise<OnboardingProgress> => {
  const response = await postWithFile('/appservice/upsertOnboardingProgress', progress, undefined);
  return response;
};

// Frontend API function (api.ts)
export const bulkDownloadTxns = async (txnIds: string[], isPackingSlip?: boolean): Promise<Blob> => {
  try {
    const queryParams: Record<string, any> = {};
    if (isPackingSlip) {
      queryParams['isPackingSlip'] = isPackingSlip;
    }

    const response = await postWithCacheClear('/txndata/bulkDownloadTxns',
      { txnIds }, // Send as JSON object
      {
        params: queryParams,
        responseType: 'blob', // Important for PDF downloads
        headers: {
          'Content-Type': 'application/json', // Explicitly set content type
        }
      }
    );

    return response;
  } catch (error) {
    handleApiError(error);
  }
};

// Add these bulk delete API functions
export const bulkDeleteTxns = async (txnIds: string[]) => {
  const response = await deleteWithCacheUpdate<BulkDeleteResponse>(APIEndpoints.BULK_DELETE_TXN, { txnIds });
  return (response as any).data;
};

export const bulkDeletePayments = async (paymentIds: string[]) => {
  const response = await deleteWithCacheUpdate<BulkDeleteResponse>(APIEndpoints.BULK_DELETE_PAYMENT, { paymentIds });
  return (response as any).data;
};

export const bulkDeleteLedgers = async (ledgerIds: string[]) => {
  const response = await deleteWithCacheUpdate<BulkDeleteResponse>(APIEndpoints.BULK_DELETE_LEDGER, { ledgerIds });
  return (response as any).data;
};

export const fetchItemAnalytics = async (
  itemId: string,
  dateRange?: string
): Promise<ItemAnalytics> => {
  const queryParams: Record<string, any> = {};
  if (dateRange) queryParams['dateRange'] = dateRange;
  return fetchData<ItemAnalytics>(APIEndpoints.GET_ITEM_ANALYTICS, [itemId], queryParams);
};
// ============================================
// SUBSCRIPTION API FUNCTIONS - Updated to match backend spec
// ============================================

/**
 * Get pricing from external pricing service
 * Uses external pricing API (zapps.zetran.com)
 */
export const getSubscriptionPricing = async (
  data: PricingRequest
): Promise<PricingResponse> => {
  const PRICING_API_URL = import.meta.env.VITE_APP_PRICING_API_URL || 'https://zapps.zetran.com';
  const response = await axios.post<PricingResponse>(`${PRICING_API_URL}/pricing/calculate`, data);
  return response.data;
};

/**
 * Get current subscription details
 * JWT token contains bizId, no need to pass in URL
 */
export const getSubscriptionDetails = async (): Promise<SubscriptionData> => {
  return fetchDataWithCacheOption<SubscriptionData>('/subscription/details', true);
};

/**
 * Create trial subscription
 * Creates a new trial subscription for the business
 */
export const createTrialSubscription = async (
  data: {
    recurrence: string;
    isTrial: boolean;
    trialEndsAt?: string;
  }
): Promise<SubscriptionData> => {
  try {
    const response = await postWithFile(
      '/subscription/create',
      data,
      undefined
    );
    return response;
  } catch (error) {
    handleApiError(error);
  }
};

/**
 * Update subscription
 * Updates subscription settings like recurrence and end date
 */
/**
 * Update subscription
 * Updates subscription settings like recurrence and end date
 */
export const updateSubscription = async (
  data: {
    recurrence?: string;
    endDate?: string;
    [key: string]: any;
  }
): Promise<SubscriptionData> => {
  return putWithCacheUpdate<SubscriptionData>('/subscription/update', data as any) as Promise<SubscriptionData>;
};

/**
 * Cancel subscription
 * Cancels active subscription (bizId from JWT token)
 */
export const cancelSubscription = async (
  reason?: string
): Promise<{ success: boolean; message: string; bizId: string }> => {
  try {
    const response = await postWithFile(
      '/subscription/cancel',
      { reason },
      undefined
    );
    return response;
  } catch (error) {
    handleApiError(error);
  }
};

/**
 * Get subscription history
 * Fetches payment and subscription change history (bizId from JWT token)
 */
export const getSubscriptionHistory = async (): Promise<SubscriptionHistoryEntry[]> => {
  return fetchData<SubscriptionHistoryEntry[]>('/subscription/history');
};

/**
 * Create checkout session
 * Initiates payment gateway checkout (bizId from JWT token)
 */
export const createCheckoutSession = async (
  params: {
    edition: string;
    countryCode: string;
    frequency: string;
    users: number;
  }
): Promise<CheckoutResponse> => {
  try {
    const payload = {
      item: 'myBooks',
      edition: params.edition,
      country_code: params.countryCode,
      frequency: params.frequency,
      users: params.users,
      method: 'hosted'
    };

    const response = await postWithFile(
      '/subscription/checkout',
      payload,
      undefined
    );

    return response;
  } catch (error) {
    handleApiError(error);
  }
};

/**
 * Confirm payment after gateway transaction
 * Verifies payment and activates subscription (bizId from JWT token)
 */
export const confirmSubscriptionPayment = async (
  data: ConfirmPaymentRequest
): Promise<ConfirmPaymentResponse> => {
  try {
    const response = await postWithFile(
      '/subscription/confirm',
      data,
      undefined
    );

    return response;
  } catch (error) {
    handleApiError(error);
  }
};

export const fetchSearch = async (q: string, limit = 5): Promise<SearchResponse> => {
  return fetchData<SearchResponse>('/search', undefined, { q, limit });
};

// ── Documents API ─────────────────────────────────────────────────────────────
export const fetchDocuments = async (group?: string): Promise<DocumentListItem[]> => {
  return fetchData<DocumentListItem[]>('/documents/list', undefined, group ? { group } : undefined);
};

export const fetchStorageInfo = async (): Promise<StorageInfo> => {
  return fetchData<StorageInfo>('/documents/storageInfo');
};

export const uploadDocument = async (file: File, metadata: UploadDocumentMetadata): Promise<DocumentListItem> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('metadata', JSON.stringify(metadata));
  const response = await postWithFile('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response;
};

export const deleteDocument = async (documentId: string): Promise<DeleteDocumentResponse> => {
  const data = await postWithCacheClear<DeleteDocumentResponse>('/documents/delete', { documentId });
  return data;
};

export const fetchBackendVersion = async (): Promise<string> => {
  try {
    const result = await fetchData<any>('/appservice/version', undefined, undefined, true);
    if (typeof result === 'string') return result;
    return result?.version ?? result?.appVersion ?? result?.buildVersion ?? '';
  } catch (error) {
    console.warn('[fetchBackendVersion] failed:', error);
    return '';
  }
};

// ── Recycle Bin API ────────────────────────────────────────────────────────

export interface RecycleBinItemRef {
  id: string;
  entityType: string;
}

export const fetchRecycleBinList = async (params: Record<string, any>): Promise<any> => {
  try {
    return fetchData<any>('/recycle/list', undefined, params);
  } catch (error: any) {
    handleApiError(error);
  }
};

export const validateRecycleBinRestore = async (items: RecycleBinItemRef[]): Promise<any> => {
  try {
    return await postWithCacheClear('/recycle/validate', { items });
  } catch (error: any) {
    handleApiError(error);
  }
};

export const restoreRecycleBinItems = async (items: RecycleBinItemRef[], acknowledgeWarnings?: boolean): Promise<any> => {
  try {
    const response = await postWithCacheClear('/recycle/restore', { items, acknowledgeWarnings });
    lastUpdatesCacheTimestamp = 0;
    return response;
  } catch (error: any) {
    handleApiError(error);
  }
};

export const permanentDeleteRecycleBinItems = async (items: RecycleBinItemRef[]): Promise<any> => {
  try {
    return await deleteWithCacheUpdate('/recycle/permanent', { items });
  } catch (error: any) {
    handleApiError(error);
  }
};

export const getUserProfile = async (): Promise<UserProfile> => {
  return fetchData<UserProfile>('/auth/profile');
};

export const updateUserProfile = async (
  data: {
    userName?: string;
    phone?: string;
    language?: string;
  }
): Promise<UserProfile> => {
  return patchData<UserProfile>('/auth/profile', data);
};

// ── Backup & Restore ──────────────────────────────────────────────────────────

export interface RestoreResult {
  mode: 'upsert' | 'replace';
  imported: {
    uoms: number;
    accounts: number;
    contacts: number;
    items: number;
    taxes: number;
    paymentTerms: number;
    priceLists: number;
    attributes: number;
    transactions: number;
    payments: number;
    ledgerEntries: number;
    settings: number;
    numberRanges: number;
    fieldConfigs: number;
    tableConfigs: number;
    kvPairs: number;
  };
  errors: string[];
}

export const downloadBackup = async (): Promise<void> => {
  const { data, headers } = await fetchBlobData('/backup/export', { timeout: 300000 });
  const blob = data instanceof Blob ? data : new Blob([data]);
  const disposition: string = headers['content-disposition'] || '';
  const match = disposition.match(/filename[^;=\n]*=['"]?([^'";\n]+)['"]?/);
  const filename = match ? match[1] : 'mybooks-backup.zip';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const restoreBackup = async (file: File, mode: 'upsert' | 'replace'): Promise<RestoreResult> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('mode', mode);
  const response = await postWithCacheClear<RestoreResult>('/backup/restore', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000, // 5 minutes
  });
  return response;
};

// ─── Manufacturing — BOMs ────────────────────────────────────────────────────

export const fetchBOMs = async (status?: BOMStatus): Promise<BOM[]> =>
  fetchData<BOM[]>('/manufacturing/boms', undefined, status ? { status } : undefined);

export const fetchBOMById = async (bomId: string): Promise<BOM> =>
  fetchDataWithCacheOption<BOM>('/manufacturing/boms/:bomId', true, { bomId });

export const upsertBOM = async (dto: Partial<BOM>): Promise<BOM> =>
  postWithCacheClear<BOM>('/manufacturing/boms', dto);

export const archiveBOM = async (bomId: string): Promise<{ success: boolean }> =>
  putWithCacheClear<{ success: boolean }>(`/manufacturing/boms/${bomId}/archive`, {});

export const deleteBOM = async (bomId: string): Promise<{ success: boolean }> =>
  deleteData<{ success: boolean }>(`/manufacturing/boms/${bomId}`);

// ─── Manufacturing — Work Orders ─────────────────────────────────────────────

export const fetchWorkOrders = async (status?: WorkOrderStatus): Promise<WorkOrder[]> =>
  fetchData<WorkOrder[]>('/manufacturing/work-orders', undefined, status ? { status } : undefined);

export const fetchWorkOrderById = async (workOrderId: string): Promise<WorkOrder> =>
  fetchDataWithCacheOption<WorkOrder>('/manufacturing/work-orders/:workOrderId', true, { workOrderId });

export const createWorkOrder = async (dto: Partial<WorkOrder>): Promise<WorkOrder> =>
  postWithCacheClear<WorkOrder>('/manufacturing/work-orders', dto);

export const updateWorkOrder = async (workOrderId: string, dto: Partial<WorkOrder>): Promise<WorkOrder> =>
  putWithCacheClear<WorkOrder>(`/manufacturing/work-orders/${workOrderId}`, dto);

export const startWorkOrder = async (workOrderId: string): Promise<WorkOrder> =>
  putWithCacheClear<WorkOrder>(`/manufacturing/work-orders/${workOrderId}/start`, {});

export const completeWorkOrder = async (
  workOrderId: string,
  dto: { outputs?: WorkOrderOutput[]; components?: WorkOrderLine[]; costLines?: WorkOrderCostLine[]; completedDate?: Date; notes?: string },
): Promise<WorkOrder> =>
  putWithCacheClear<WorkOrder>(`/manufacturing/work-orders/${workOrderId}/complete`, dto);

export const cancelWorkOrder = async (workOrderId: string): Promise<WorkOrder> =>
  putWithCacheClear<WorkOrder>(`/manufacturing/work-orders/${workOrderId}/cancel`, {});

export const fetchWorkOrderLedger = async (workOrderId: string): Promise<Ledger> =>
  fetchDataWithCacheOption<Ledger>(`/manufacturing/work-orders/${workOrderId}/ledger`);

export interface WorkOrderViewResponse {
  workOrder: WorkOrder;
  renderWorkOrder: string;
}

export const getWorkOrderView = async (workOrderId: string): Promise<WorkOrderViewResponse> =>
  fetchDataWithCacheOption<WorkOrderViewResponse>('/manufacturing/work-orders/:workOrderId/view', true, { workOrderId });

export const getWorkOrderPdf = async (workOrderId: string): Promise<Blob> => {
  const { data } = await fetchBlobData(`/manufacturing/work-orders/${workOrderId}/pdf`, { responseType: 'arraybuffer' });
  return new Blob([data], { type: 'application/pdf' });
};

export const downloadWorkOrderPdf = async (workOrderId: string, workOrderNo?: string): Promise<void> => {
  const pdfBlob = await getWorkOrderPdf(workOrderId);
  const url = window.URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${workOrderNo || workOrderId}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ─── Projects ─────────────────────────────────────────────────────────────────

export const fetchProjects = async (status?: ProjectStatus, clientId?: string, silent?: boolean): Promise<Project[]> =>
  fetchData<Project[]>('/projects', undefined, { ...(status && { status }), ...(clientId && { clientId }) }, silent);

export const fetchProjectBillingStatus = async (status?: ProjectStatus, clientId?: string): Promise<any[]> =>
  fetchData<any[]>('/projects/billing-status', undefined, { ...(status && { status }), ...(clientId && { clientId }) });

export const fetchProjectById = async (projectId: string): Promise<Project> =>
  fetchDataWithCacheOption<Project>('/projects/:projectId', true, { projectId });

export const upsertProject = async (dto: Partial<Project>, files?: File[]): Promise<Project> => {
  const formData = new FormData();
  formData.append('projectDto', JSON.stringify(dto));
  (files || []).forEach(file => formData.append('files', file));

  const response = await postWithFile('/projects', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response;
};

export const fetchProjectAttachments = async (projectId: string): Promise<ProjectAttachment[]> => {
  const response = await fetchDataWithCacheOption<ProjectAttachment[]>(`/projects/${projectId}/attachments`, true);
  return response;
};

export const updateProjectStatus = async (projectId: string, status: ProjectStatus): Promise<Project> =>
  putWithCacheClear<Project>(`/projects/${projectId}/status`, { status });

export const deleteProject = async (projectId: string): Promise<{ success: boolean }> =>
  deleteData<{ success: boolean }>(`/projects/${projectId}`);

export const upsertProjectMilestones = async (projectId: string, milestones: ProjectMilestone[]): Promise<Project> =>
  putWithCacheClear<Project>(`/projects/${projectId}/milestones`, { milestones });

export const updateMilestoneStatus = async (projectId: string, milestoneId: string, status: string): Promise<Project> =>
  putWithCacheClear<Project>(`/projects/${projectId}/milestones/${milestoneId}/status`, { status });

export const fetchProjectTxns = async (projectId: string): Promise<{ txns: any[]; payments: any[] }> =>
  fetchDataWithCacheOption<{ txns: any[]; payments: any[] }>(`/projects/${projectId}/txns`, true);

export const fetchProjectSummary = async (projectId: string): Promise<ProjectSummary> =>
  fetchDataWithCacheOption<ProjectSummary>(`/projects/${projectId}/summary`, true);

// ─── Budgets ──────────────────────────────────────────────────────────────────

export const fetchBudgets = async (financialYear?: string, status?: BudgetStatus): Promise<Budget[]> =>
  fetchData<Budget[]>('/budget', undefined, { ...(financialYear && { financialYear }), ...(status && { status }) });

export const fetchBudgetById = async (budgetId: string): Promise<Budget> =>
  fetchDataWithCacheOption<Budget>(`/budget/${budgetId}`, true);

export const upsertBudget = async (dto: Partial<Budget>): Promise<Budget> =>
  postWithCacheClear<Budget>('/budget', dto);

export const updateBudget = async (budgetId: string, dto: Partial<Budget>): Promise<Budget> =>
  putWithCacheClear<Budget>(`/budget/${budgetId}`, dto);

export const deleteBudget = async (budgetId: string): Promise<void> =>
  deleteData(`/budget/${budgetId}`);

export const duplicateBudget = async (budgetId: string, inflatePercent?: number): Promise<Budget> =>
  postWithCacheClear<Budget>(`/budget/${budgetId}/duplicate`, { inflatePercent });

export const activateBudget = async (budgetId: string): Promise<Budget> =>
  postWithCacheClear<Budget>(`/budget/${budgetId}/activate`, {});

export const fetchBudgetVsActual = async (budgetId: string): Promise<BudgetVsActualResponse> =>
  fetchDataWithCacheOption<BudgetVsActualResponse>(`/budget/${budgetId}/vs-actual`, true);

// Always bypass the 5-minute HTTP cache: the widget's presence flips as soon as a budget is
// created/activated (or its FY changes), and a cached null kept the dashboard card hidden until expiry.
export const fetchBudgetDashboardWidget = async (): Promise<BudgetDashboardWidget | null> =>
  fetchDataWithCacheOption<BudgetDashboardWidget | null>('/budget/dashboard-widget', true);

// Public payment APIs — no auth token required
const publicAxios = axios.create({ baseURL: import.meta.env.VITE_APP_API_BASE_URL });

export const publicPaymentInfo = async (txnId: string): Promise<{ hasGateway: boolean; balanceDue?: number; currency?: string }> => {
  const res = await publicAxios.get(`/payments/public/info/${txnId}`);
  return res.data;
};

export const publicInitiatePayment = async (txnId: string, amount: number): Promise<PublicPaymentInitResult> => {
  const res = await publicAxios.post('/payments/public/initiate', { txnId, amount });
  return res.data;
};

export const publicConfirmPayment = async (logId: string, gatewayPaymentId: string, signature?: string, gatewayOrderId?: string): Promise<{ success: boolean; newBalance: number; status: string }> => {
  const res = await publicAxios.post('/payments/public/confirm', { logId, gatewayPaymentId, signature, gatewayOrderId });
  return res.data;
};

export const publicPaymentStatus = async (logId: string): Promise<PublicPaymentStatus> => {
  const res = await publicAxios.get(`/payments/public/status/${logId}`);
  return res.data;
};

// Email preferences (MBAI-548) — no auth token required, access is via the signed
// unsubscribe token itself (same pattern as the payment-public endpoints above).
export interface EmailTypeInfo {
  type: string;
  label: string;
  description: string;
  optedOut: boolean;
}

export interface EmailPreferencesView {
  email: string;
  highlightType?: string;
  optionalTypes: EmailTypeInfo[];
  mandatoryCategories: string[];
}

export const submitDemoLead = async (email: string, captchaToken: string, marketingOptIn: boolean): Promise<void> => {
  await publicAxios.post('/demo/lead', { email, captchaToken, marketingOptIn });
};

export const fetchEmailPreferences = async (token: string): Promise<EmailPreferencesView> => {
  const res = await publicAxios.get(`/email-preferences/public/${token}`);
  return res.data;
};

export const saveEmailPreferences = async (token: string, optedOutTypes: string[]): Promise<EmailPreferencesView> => {
  const res = await publicAxios.patch(`/email-preferences/public/${token}`, { optedOutTypes });
  return res.data;
};

export const fetchGatewayConfig = async (): Promise<GatewayConfig> =>
  fetchDataWithCacheOption<GatewayConfig>('/settings/payment-gateway', true);

export const saveGatewayConfig = async (config: Partial<GatewayConfig> & { activeProvider: string }): Promise<{ success: boolean }> =>
  postWithCacheClear<{ success: boolean }>('/settings/payment-gateway', config);

// MBAI-177: these four must go through axiosInstance (baseURL + auth header set up in
// setupAxios()), not the raw axios package — a bare axios.get/post with a relative path
// resolves against the frontend's own origin, not the backend API host, and carries no
// Authorization header (all four were silently hitting the wrong host before this fix).
export const testGatewayConnection = async (dto: { provider: string } & Partial<GatewayConfig>): Promise<TestGatewayConnectionResult> => {
  const res = await axiosInstance.post('/settings/payment-gateway/test', dto);
  return res.data;
};

export const fetchGatewayTransactionLogs = async (page = 1, limit = 50): Promise<{ logs: any[]; total: number; page: number; limit: number }> => {
  const res = await axiosInstance.get('/settings/payment-gateway/transactions', { params: { page, limit } });
  return res.data;
};

export const initiateStripeConnectOnboard = async (returnUrl: string): Promise<{ url: string }> => {
  const res = await axiosInstance.post('/settings/payment-gateway/stripe-connect/onboard', { returnUrl });
  return res.data;
};

export const completeStripeConnectOnboard = async (code: string): Promise<{ success: boolean; accountId: string }> => {
  const res = await axiosInstance.get('/settings/payment-gateway/stripe-connect/callback', { params: { code } });
  return res.data;
};

export const warmupPdfLambda = (): void => {
  axiosInstance.get('/utils/warmPdf').catch(() => {});
};

export const submitContactUs = async (data: { name: string; email: string; phone?: string; subject: string; message: string }): Promise<{ issueKey: string }> =>
  postWithCacheClear('/support/contact-us', data);

export const submitFeedback = async (data: { rating: number; comment?: string; context?: { screen?: string; feature?: string } }): Promise<{ feedbackId: string }> =>
  postWithCacheClear('/feedback', data);

